import { createApi, fetchBaseQuery, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn } from "@reduxjs/toolkit/query";
import { logEvent } from "./logger";
import { setTokens, signOut } from "../features/auth/authSlice";
import { API_BASE_URL } from "./apiConfig";

const baseUrl = API_BASE_URL;

const describeRequest = (args: string | FetchArgs): string => {
    if (typeof args === "string") {
        return args;
    }
    const method = (args.method ?? "GET").toUpperCase();
    return `${method} ${args.url}`;
};

const debugApiEnabled =
    typeof window !== "undefined" && (window as unknown as { __debugApi?: boolean }).__debugApi === true;

const rawBaseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
        const state = getState() as {
            auth: { accessToken: string | null; profile?: { coachingId?: string | null } | null };
            tenant: { slug: string | null };
        };
        const token = state.auth?.accessToken;
        if (token) {
            headers.set("Authorization", `Bearer ${token}`);
        }
        // NOTE: backend validates this header against user.coaching_id and rejects
        // mismatches with 403 "Tenant mismatch". Once authenticated, the user's own
        // coaching_id (from their profile/JWT) is the source of truth — it must take
        // priority over the hostname-derived tenant slug (which resolves to the
        // super-admin slug on plain "localhost", breaking every coaching_admin request).
        const coachingId = state.auth?.profile?.coachingId || state.tenant?.slug;
        if (coachingId) {
            headers.set("X-Coaching-Id", coachingId);
        }
        return headers;
    }
});

const shouldForceLogout = (error: FetchBaseQueryError): boolean => {
    if (error.status !== 401) {
        return false;
    }
    const payload = "data" in error ? error.data : null;
    const detail = typeof payload === "string" ? payload : (payload as { detail?: string })?.detail;
    return detail === "Invalid token";
};

const triggerForcedLogout = (dispatch: (action: unknown) => unknown) => {
    dispatch(signOut());
    if (typeof window !== "undefined" && window.location.pathname !== "/auth") {
        window.location.replace("/auth");
    }
};

/**
 * Exchange the refresh token for a new pair, at most once at a time.
 *
 * WHY THIS EXISTS
 * The access token lasts 30 minutes and the refresh token 24 hours. The refresh
 * token was being SAVED and never used, so every session ended at the 30-minute
 * mark: a 401 tripped the forced logout and the student was thrown to the login
 * screen mid-question, with a perfectly valid refresh token sitting in
 * localStorage. Observed in a normal session, which is exactly how a student
 * would meet it.
 *
 * WHY IT IS SINGLE-FLIGHT
 * A page in normal use has several requests in the air. Without the shared
 * promise, all of them 401 at the same moment and each fires its own refresh —
 * the first rotates the token and the rest present one that has just been
 * replaced, so a burst of parallel requests would log the student out *because*
 * the refresh worked.
 */
let refreshInFlight: Promise<string | null> | null = null;

const refreshAccessToken = async (
    api: Parameters<BaseQueryFn>[1]
): Promise<string | null> => {
    if (refreshInFlight) return refreshInFlight;

    refreshInFlight = (async () => {
        const state = api.getState() as { auth: { refreshToken: string | null } };
        const refresh = state.auth?.refreshToken ?? localStorage.getItem("refreshToken");
        if (!refresh) return null;

        try {
            // Deliberately a bare fetch rather than the wrapped base query:
            // routing it back through this interceptor would let a failing
            // refresh trigger another refresh.
            const response = await fetch(`${baseUrl}/auth/refresh`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ refresh_token: refresh })
            });
            if (!response.ok) return null;

            const pair = await response.json();
            if (!pair?.access_token) return null;
            api.dispatch(
                setTokens({ access: pair.access_token, refresh: pair.refresh_token ?? refresh })
            );
            return pair.access_token as string;
        } catch {
            return null;
        } finally {
            // Cleared here rather than after the await chain above, so a failed
            // refresh does not pin every later 401 to the same rejected promise.
            refreshInFlight = null;
        }
    })();

    return refreshInFlight;
};

const instrumentedBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions
) => {
    if (debugApiEnabled) {
        // eslint-disable-next-line no-console
        console.log("[api] request", {
            baseUrl,
            args
        });
    }
    try {
        const result = await rawBaseQuery(args, api, extraOptions);
        if (debugApiEnabled) {
            // eslint-disable-next-line no-console
            console.log("[api] response", {
                request: describeRequest(args),
                error: result.error ?? null,
                ok: !result.error
            });
        }
        if (result.error) {
            // An expired token is not a dead session — it is a token that needs
            // replacing. Try once, then retry the original request; only sign
            // out if the refresh itself fails.
            if (shouldForceLogout(result.error)) {
                const renewed = await refreshAccessToken(api);
                if (renewed) {
                    const retried = await rawBaseQuery(args, api, extraOptions);
                    if (!retried.error) return retried;
                }
                triggerForcedLogout(api.dispatch);
            }
            logEvent({
                level: "ERROR",
                component: "api",
                message: `Request failed: ${describeRequest(args)}`,
                details: {
                    status: result.error.status,
                    data: (result.error as FetchBaseQueryError).data ?? null
                }
            });
        }
        return result;
    } catch (error) {
        const fallback: FetchBaseQueryError = { status: "FETCH_ERROR", error: String(error) };
        logEvent({
            level: "ERROR",
            component: "api",
            message: `Network error during ${describeRequest(args)}`,
            details: { error: String(error) }
        });
        return { error: fallback };
    }
};

/**
 * Payload accepted by the /auth/login endpoint.
 */
export interface LoginRequest {
    email: string;
    password: string;
}

export interface TokenPair {
    access_token: string;
    refresh_token: string;
    token_type: string;
}

/**
 * Response envelope produced by /auth/login combining tokens and the public user profile.
 */
export interface LoginResponse {
    tokens: TokenPair;
    user: UserPublic;
}

export type UserRole = "super_admin" | "coaching_admin" | "school_admin" | "teacher" | "user" | "student";
export type ResearchEngineName = "guided" | "storm";
export type ResearchProjectStatus = "planning" | "researching" | "review_ready" | "drafting" | "draft_ready" | "complete";

export interface ResearchPolicy {
    advanced_storm_enabled?: boolean;
    enabled: boolean;
    student_enabled: boolean;
    web_discovery_enabled: boolean;
    engine: ResearchEngineName;
    daily_project_limit: number;
    max_sources_per_project: number;
    require_outline_approval: boolean;
}

/** A single student's exception to their coaching's policy. Every field
 *  `null`/absent means "inherit the coaching's own setting" — an override is
 *  a diff, not a second full policy. Mirrors ResearchPolicyOverride. */
export interface ResearchPolicyOverride {
    student_enabled?: boolean | null;
    web_discovery_enabled?: boolean | null;
    daily_project_limit?: number | null;
    max_sources_per_project?: number | null;
    require_outline_approval?: boolean | null;
    advanced_storm_enabled?: boolean | null;
}

/** One row in the admin's per-student list. */
export interface ResearchStudentPolicyView {
    user_id: string;
    full_name: string;
    email: string;
    has_override: boolean;
    effective: ResearchPolicy;
    override: ResearchPolicyOverride;
}

export interface ResearchOutlineSection {
    section_id: string;
    heading: string;
    questions: string[];
}

export interface ResearchSource {
    source_id: string;
    title: string;
    url: string;
    snippet: string;
    provider: string;
    retrieved_at: string;
    /** Bibliographic details, only when the provider returned them. */
    authors?: string[];
    year?: number | null;
    venue?: string | null;
    doi?: string | null;
}

/** Reference styles of the report: the ONE frontend list (backend twin:
 *  utils/reference_styles.py ReferenceStyle). The type, the picker and the
 *  check on a remembered choice all come from here. */
export const REFERENCE_STYLES = [
    { value: "apa", label: "APA 7" },
    { value: "ieee", label: "IEEE" },
    { value: "harvard", label: "Harvard" },
    { value: "mla", label: "MLA 9" }
] as const;
export type ReferenceStyle = (typeof REFERENCE_STYLES)[number]["value"];
export const DEFAULT_REFERENCE_STYLE: ReferenceStyle = "apa";
export const isReferenceStyle = (value: string): value is ReferenceStyle => REFERENCE_STYLES.some((style) => style.value === value);

/** The report as the Word export lays it out in one reference style. */
export interface StyledResearchReport {
    style: ReferenceStyle;
    sections: { section_id: string; heading: string; body: string; sources: string }[];
    references: { source_id: string; number: number | null; text: string }[];
    licence_notice: string | null;
}

export interface ResearchClaim {
    claim_id: string;
    text: string;
    source_ids: string[];
    support: "supported" | "needs_review" | "contradicted";
    review_note: string;
    /** An AI reading of each inspected source against this claim (evidence strength). */
    stances?: { source_id: string; stance: "supports" | "challenges" | "unrelated" }[];
}

export interface ResearchDraftSection {
    citation_source_ids?: string[];
    section_id: string;
    heading: string;
    body: string;
    claim_ids: string[];
}

export interface ResearchRecallItem {
    question_id: string;
    question: string;
    answer: string;
    source_ids: string[];
}

export interface ResearchDiscourseTurn {
    turn_id: string;
    speaker: "moderator" | "expert" | "student";
    text: string;
    source_ids: string[];
    created_at: string;
    /** Written by the Co-STORM seminar's AI expert/moderator (30-Sep-2026). */
    ai?: boolean;
    /** Expert turns: whether the answer cites a source. */
    cited?: boolean | null;
}

export interface ResearchConceptNode {
    node_id: string;
    label: string;
    children: ResearchConceptNode[];
}

export interface ResearchProject {
    draft_engine?: ResearchEngineName;
    advanced_run?: ResearchAdvancedRun | null;
    /** Up to the last 5 replaced runs, newest first — read-only, so a
     *  student can revisit an earlier advanced attempt without re-running
     *  (and re-paying for) it. */
    advanced_run_history: ResearchAdvancedRun[];
    archived?: boolean;
    archived_at?: string | null;
    project_id: string;
    topic: string;
    learning_goal: string;
    audience_level: string;
    language: string;
    status: ResearchProjectStatus;
    outline_approved: boolean;
    perspectives: string[];
    outline: ResearchOutlineSection[];
    sources: ResearchSource[];
    claims: ResearchClaim[];
    draft_sections: ResearchDraftSection[];
    recall_items: ResearchRecallItem[];
    discourse: ResearchDiscourseTurn[];
    concept_map: ResearchConceptNode[];
    polished: boolean;
    created_at: string;
    updated_at: string;
    storm_notes?: ResearchStormNote[];
    storm_notes_source_ids?: string[];
    /** Agreement meters for the question map's yes/no questions. */
    question_meters?: { section_id: string; question: string; answers: { source_id: string; answer: "yes" | "no" | "mixed" | "not_addressed" }[] }[];
    evidence_checked_at?: string | null;
}

export interface ResearcherSummary {
    user_id: string;
    full_name: string;
    email: string;
    role: string;
    coaching_id: string;
    coaching_name: string;
    project_count: number;
    sources_collected: number;
    topics: string[];
    first_active_at: string | null;
    last_active_at: string | null;
    /** The owner's account no longer exists. Shown as such rather than blank. */
    user_missing: boolean;
}

export interface ResearchCapabilities {
    advanced_storm_enabled?: boolean;
    active_engine: ResearchEngineName;
    free_mode: boolean;
    web_discovery_enabled: boolean;
    storm_available: boolean;
    storm_reason: string;
}

export interface ResearchAdvancedRun {
    run_id: string;
    state: "queued" | "running" | "ready" | "failed" | "cancelled" | "applied";
    config_id: string;
    model_name: string;
    provider: string;
    source_ids: string[];
    total_sections: number;
    model_calls: number;
    message: string;
    /** Why a failed run failed, when the student can act on it: the model's provider refused
     *  (key rejected, payment required, quota used up). A category only, never provider text. */
    failure_kind?: "model_account" | null;
    updated_at: string;
    sections: { section_id: string; heading: string; body: string; source_ids: string[] }[];
    /** "explore" = STORM's research conversations + a suggested question map;
     *  "write" = the report. Older runs have no kind and are "write". */
    kind?: "explore" | "write";
    max_calls?: number;
    turns_done?: number;
    proposed_outline?: ResearchOutlineSection[];
    /** STORM's cited lead section, placed first in the report on adoption. */
    summary?: string | null;
}

/** One question and answer from STORM's research conversations. */
export interface ResearchStormNote {
    persona: string;
    question: string;
    answer: string;
    /** False when the answer carries no [n] citation - shown, never used in the report. */
    cited: boolean;
}

export interface DecisionModelState {
    enabled: boolean;
    has_api_key: boolean;
    updated_at: string | null;
    updated_by: string | null;
}

export interface ResearchLibrarySaveOptions {
    subject?: string | null;
    institution?: string | null;
    share_with_coaching?: boolean;
    reference_style?: ReferenceStyle;
}

export interface ResearchLibrarySaveResult {
    source_id: string;
    status: string;
    /** Every cited source is openly licensed, so it MAY go to the coaching. */
    shareable: boolean;
    share_requested: boolean;
    /** Cited sources that keep it private (licence unknown or restrictive). */
    blocking_sources: string[];
    /** "Researched by Asha K. · Demo Academy · Delhi University". */
    credit: string;
}

export interface ResearchTeachBack {
    score: number;
    feedback: string;
    covered: string[];
    next_step: string;
    /** Plan questions the explanation does not answer yet. */
    missing: string[];
    /** How the score is worked out, in plain words. */
    criteria: string;
    /** This student's scores on the project, oldest first. */
    attempts: number[];
    best: number;
    /** Other students on a similar topic - only when 3 or more exist. */
    peers?: { average: number; best: number; students: number } | null;
}
export type AdminCreatableRole = Extract<UserRole, "coaching_admin" | "school_admin" | "user">;


export interface TenantBranding {
    tenant_slug: string;
    display_name: string;
    theme_primary: string;
    theme_accent: string;
    logo_url?: string;
    tagline?: string | null;
    feature_flags: Record<string, boolean>;
    /** "coaching" | "school"; null for the platform itself. */
    org_type?: "coaching" | "school" | null;
}

/**
 * What an admin may change about how their portal looks.
 *
 * Every field is optional: changing only the accent colour must not blank the
 * display name. `logo_url: ""` CLEARS the logo -- omitting it leaves it alone.
 */
export interface TenantBrandingUpdate {
    display_name?: string;
    theme_primary?: string;
    theme_accent?: string;
    logo_url?: string;
}

export type QuestionType = "mcq_single" | "mcq_multi" | "true_false" | "integer" | "short_answer";

export interface SourceCitation {
    source_id: string;
    chunk_id: string;
    page?: number | null;
    heading?: string | null;
    excerpt: string;
    match_score?: number;
}

export interface QuizQuestion {
    question_id: string;
    text: string;
    options: string[];
    correct_option?: number | null;
    correct_options?: number[] | null;
    numeric_answer?: number | null;
    numeric_tolerance?: number | null;
    model_answer_text?: string | null;
    explanation?: string | null;
    /** The "how to solve this fast" teacher tip, shown in the post-attempt review — distinct from `explanation`. */
    quick_solve_trick?: string | null;
    subject?: string | null;
    sequence?: number | null;
    question_type?: QuestionType | string;
    image_url?: string | null;
    /** Index-aligned with `options`; null where an option is text-only. Lets a
     *  question ask "which of these figures is correct" without faking it as one
     *  composite picture — which breaks option shuffling and distractor stats. */
    option_images?: (string | null)[] | null;
    source_citation?: SourceCitation | null;
    marks?: number;
    negative_marks?: number;
}

export interface QuizSummary {
    quiz_id: string;
    coaching_id: string;
    title: string;
    status: string;
    /** The exam taxonomy the paper was filed under. Present on the server's
     *  `QuizSummary` since the taxonomy landed; the TypeScript mirror simply
     *  never declared them, so the builder could not read back what it had
     *  itself saved. */
    exam_category?: string | null;
    grade_level?: string | null;
    tags?: string[];
    /** Set while an admin is holding a running paper.
     *
     *  A hold stops the paper being STARTED; it does not stop the clock of a
     *  student already sitting it, and it deliberately does not change
     *  `status` — a held paper is still `live`, so the Expired tab and the
     *  status sweeper keep agreeing with reality. */
    paused_at?: string | null;
    /** Set when the AUTHOR archived the paper: off every list, no new
     *  sittings, results kept. Reversible. */
    archived_at?: string | null;
    exam_id?: string | null;
    subject_ids: string[];
    go_live_at: string;
    close_at: string;
    duration_minutes: number;
    publish_answer_sheet_at: string;
    result_visibility: string;
    source_file_uri?: string | null;
    /** EMPTY on list endpoints (`/quizzes/live`, `/quizzes/`) by design — a
     *  listing shows a count, not the paper. Read `question_count` for the
     *  number; `questions` is populated only on single-quiz responses. */
    questions: QuizQuestion[];
    /** Always populated, including where `questions` is deliberately empty. */
    question_count?: number;
    ai_solutions_enabled?: boolean;
    /** "private" = a self-practice paper only its author sees. */
    visibility?: "private" | "shared";
    /** Author. The server has always sent it; the type omitted it, so the UI
     *  could not tell a student's own practice paper from their coaching's
     *  without a second request. */
    created_by_id?: string | null;
    /** "super_admin" | "coaching_admin" | "student" — which KIND of author, so
     *  a card can say where the paper came from without a lookup per paper. */
    created_by_role?: string | null;
}

export interface QuizCreatePayload {
    coaching_id: string;
    exam_id?: string | null;
    subject_ids?: string[];
    title: string;
    duration_minutes: number;
    go_live_at: string;
    close_at: string;
    publish_answer_sheet_at: string;
    result_visibility?: string;
    source_file_uri?: string | null;
    questions: QuizQuestion[];
    exam_category?: string | null;
    grade_level?: string | null;
    tags?: string[];
    ai_solutions_enabled?: boolean;
    /** "private" = a self-practice paper needing no admin grant or approval. */
    visibility?: "private" | "shared";
}

export interface QuizIngestionPreview {
    source_file_uri: string;
    total_questions: number;
    warnings: string[];
    questions: QuizQuestion[];
}

export interface QuizAttemptResult {
    quiz_id: string;
    student_id: string;
    attempted: number;
    correct: number;
    incorrect: number;
    accuracy: number;
    score: number;
    total_questions: number;
    submitted_at: string;
}

export interface QuizQuestionPublic {
    question_id: string;
    text: string;
    options: string[];
    subject?: string | null;
    sequence?: number | null;
    question_type?: QuestionType | string;
    image_url?: string | null;
    /** Present during the live attempt, unlike anything answer-key related:
     *  an option's picture IS the option. */
    option_images?: (string | null)[] | null;
    marks?: number;
}

/** One answer as the player holds it.
 *
 *  `null` means the student deliberately cleared this question — distinct from
 *  the key being absent, which means the client never had it. The server treats
 *  the two differently: absent keys are refilled from the autosaved sheet, null
 *  ones are respected as a skip. */
export type QuizAnswerValue = number | number[] | string | null;

export interface QuizPlayable {
    quiz_id: string;
    coaching_id: string;
    title: string;
    status: string;
    duration_minutes: number;
    go_live_at: string;
    close_at: string;
    server_time: string;
    questions: QuizQuestionPublic[];
    already_submitted: boolean;
    attempt_id?: string | null;
    saved_answers: Record<string, QuizAnswerValue>;
    /** Question ids the student flagged to revisit, restored on resume. */
    marked_for_review: string[];
    started_at?: string | null;
    resume_token?: string | null;
    /** Before Start: the latest moment this sitting's clock may begin (first
     *  opening + the rules-reading allowance); the server counts from it. */
    latest_start_at?: string | null;
    /** When the score becomes visible. Needed at SUBMIT time so the player can
     *  tell a student whether to look now or come back — neither field says
     *  anything about the score itself. */
    publish_answer_sheet_at?: string | null;
    result_visibility?: string | null;
}

export interface QuizProgressState {
    quiz_id: string;
    resume_token: string;
    saved_answers: number;
    marked_for_review: number;
    started_at: string;
    last_saved_at: string;
}

export interface QuizReviewQuestion {
    question_id: string;
    text: string;
    options: string[];
    /** Index-aligned with `options`; null where an option is text-only. */
    option_images?: (string | null)[] | null;
    question_type?: QuestionType | string;
    correct_option?: number | null;
    correct_options?: number[] | null;
    numeric_answer?: number | null;
    model_answer_text?: string | null;
    selected_answer?: number | number[] | string | null;
    is_correct: boolean;
    needs_manual_review?: boolean;
    explanation?: string | null;
    quick_solve_trick?: string | null;
    /** AI-written fallbacks, present only after submission when the coaching enabled them. */
    ai_explanation?: string | null;
    ai_quick_solve_trick?: string | null;
    /** Index-aligned with `options`: why each choice is right or wrong. */
    option_rationales?: string[] | null;
    ai_option_rationales?: string[] | null;
    /** Alternative ways to solve the same question. Already filtered
     *  server-side to reviewed/verified methods — never filter for
     *  correctness here, only for presentation. */
    solution_methods?: SolutionMethod[];
}

/** One generated method awaiting review, with the question it explains. */
export interface SolutionMethodReviewRow {
    question_id: string;
    question_text: string;
    sequence?: number | null;
    options: string[];
    correct_indexes: number[];
    subject?: string | null;
    /** The teacher's own solution, so a reviewer can tell whether the generated
     *  method is genuinely different or just a restatement. */
    existing_explanation?: string | null;
    existing_trick?: string | null;
    method: SolutionMethod;
}

export interface SolutionMethodQueue {
    quiz_id: string;
    title: string;
    rows: SolutionMethodReviewRow[];
    /** Counts across the whole paper, not the filtered view. */
    draft: number;
    auto_verified: number;
    human_approved: number;
    rejected: number;
}

/** One question where a blind second solve disagreed with the stored key.
 *  Carries BOTH answers because the reviewer's job is to decide which is
 *  right — a flag with no argument attached is noise people learn to dismiss. */
export interface KeyVerificationRow {
    question_id: string;
    question_text: string;
    sequence?: number | null;
    options: string[];
    subject?: string | null;
    question_type: string;
    /** What the paper says. */
    correct_indexes: number[];
    stored_answer_text?: string | null;
    /** What the verifier said, having never been shown the above. */
    verdict: string;
    model_answer?: string | null;
    model_working?: string | null;
    confidence?: string | null;
    verified_at?: string | null;
}

export interface KeyVerificationQueue {
    quiz_id: string;
    title: string;
    rows: KeyVerificationRow[];
    disputed: number;
    agreed: number;
    /** Reported deliberately: "0 disputed" only means the key is sound if
     *  everything was actually checked. An unchecked paper and a clean paper
     *  must never look the same. */
    unchecked: number;
}

export interface SolutionMethodReviewRequest {
    question_id: string;
    method_id: string;
    approve: boolean;
    /** Any of these marks the method human-authored — review means a reviewer
     *  can FIX a nearly-right method, not only vote it down. */
    label?: string;
    steps?: string[];
    applicability?: string;
    fails_when?: string;
    note?: string;
}

/** One student's line on the admin's result roster. */
export interface QuizResultRosterRow {
    student_id: string;
    student_name?: string | null;
    student_email?: string | null;
    score: number;
    correct: number;
    incorrect: number;
    attempted: number;
    total_questions: number;
    /** Correct as a share of what they ANSWERED, not of the paper — leaving
     *  questions blank is a different mistake from getting them wrong. */
    accuracy: number;
    submitted_at?: string | null;
    needs_manual_review?: boolean;
}

export interface QuizResultRoster {
    quiz_id: string;
    title: string;
    total_questions: number;
    rows: QuizResultRosterRow[];
    total: number;
    skip: number;
    limit: number;
    /** Cohort-wide, not page-wide. Null when there are no attempts. */
    average_score?: number | null;
    highest_score?: number | null;
    lowest_score?: number | null;
    average_accuracy?: number | null;
}

export interface QuizOptionBreakdown {
    index: number;
    text: string;
    is_correct: boolean;
    picked: number;
    share: number;
}

export interface QuizItemAnalysis {
    question_id: string;
    sequence?: number | null;
    text: string;
    subject?: string | null;
    attempts: number;
    correct: number;
    /** Share who got it right: high means easy. */
    p_value: number;
    /** Top-27% correct rate minus bottom-27%. Negative means the question
     *  favoured the students who did worse overall — usually a broken key. */
    discrimination?: number | null;
    options: QuizOptionBreakdown[];
    possible_bad_key: boolean;
    flag_reason?: string | null;
}

export interface QuizItemAnalysisReport {
    quiz_id: string;
    title: string;
    attempts: number;
    /** False when the cohort is too small for the numbers to mean anything. */
    statistics_reliable: boolean;
    minimum_attempts: number;
    items: QuizItemAnalysis[];
}

/** One technique for solving a question, distinct from the teacher's own
 *  worked solution. The point of the list is that a student can CHOOSE: a
 *  rigorous method they can always fall back on, and a fast one for when the
 *  clock is against them. */
export interface SolutionMethod {
    method_id: string;
    /** Technique family — drives the icon and the tier grouping. */
    method_type: string;
    /** Imperative name of the technique, e.g. "Compare only the last digit". */
    label: string;
    /** One line on what this buys you. */
    tagline?: string | null;
    est_seconds?: number | null;
    /** When this technique is valid here. */
    applicability?: string | null;
    /** When it would give the wrong answer — a shortcut without this is a trap. */
    fails_when?: string | null;
    steps: string[];
    why_it_works?: string | null;
    /** author | ai | explanation_lab — shown, because a student weighs a
     *  teacher's method differently from a machine's. */
    source: string;
    status: string;
    rank: number;
    /** Why a validator passed or rejected it. Shown on the review screen so a
     *  reviewer knows what the machine already checked; never shown to
     *  students. */
    validation_notes?: string | null;
    /** True when a machine check confirmed the technique reaches the keyed
     *  answer. Null means no automatic check exists for this technique — which
     *  is not the same as passing. */
    reaches_key?: boolean | null;
}

export interface QuizResultPublic {
    quiz_id: string;
    title: string;
    result_visibility: string;
    available: boolean;
    message?: string | null;
    attempted?: number | null;
    correct?: number | null;
    incorrect?: number | null;
    total_questions?: number | null;
    score?: number | null;
    accuracy?: number | null;
    submitted_at?: string | null;
    /** Share of the cohort this score beats (ties count half); null below the
     *  server's minimum cohort size, when `cohort_size` says how many sat it. */
    percentile?: number | null;
    cohort_size?: number | null;
    review: QuizReviewQuestion[];
}

/** Every `/page` endpoint returns this shape, so `DataTable` reads them all
 *  the same way. A per-resource envelope would be five near-identical types
 *  that drift, and a mapping per endpoint to read them. */
/** A row of a coaching's student roster, as `/students/page` returns it. */
export interface StudentRosterRow {
    student_id: string;
    coaching_id: string;
    full_name: string;
    email: string;
    status: string;
    last_updated?: string | null;
}

export interface Page<T> {
    items: T[];
    total: number;
    limit: number;
    offset: number;
}

export interface PagedListArgs {
    limit?: number;
    offset?: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc";
    [extra: string]: string | number | undefined;
}

export interface PagedAttempts {
    items: QuizAttemptSummary[];
    total: number;
    limit: number;
    offset: number;
}

export interface TranslationPublic {
    translation_id: string;
    question_id: string;
    language: string;
    status: "draft" | "approved" | "rejected";
    source_text: string;
    source_options: string[];
    text: string;
    options: string[];
    translated_by: string;
    model_name?: string | null;
    reviewed_by_name?: string | null;
    reviewed_at?: string | null;
    /** False when the question changed after this was drafted — approving a
     *  stale row would put a confidently wrong stem in front of students. */
    is_current: boolean;
}

export interface TranslationCoverage {
    language: string;
    total_questions: number;
    approved: number;
    drafted: number;
    stale: number;
}

export interface QuizAttemptSummary {
    attempt_id: string;
    quiz_id: string;
    quiz_title: string;
    /** NULL until the paper's results are released. The server withholds these
     *  three until `publish_answer_sheet_at` passes or the paper is explicitly
     *  published — the same embargo `/result` applies. `attempted` is not a
     *  mark, so it is always present. */
    score: number | null;
    attempted: number;
    correct: number | null;
    incorrect: number | null;
    total_questions: number;
    submitted_at: string;
    result_visibility: string;
    /** Who wrote the paper this attempt belongs to. Present so "papers I sat"
     *  and "papers I wrote and sat" can be separated client-side. */
    created_by_id?: string | null;
    created_by_role?: string | null;
    visibility?: string | null;
}

export type MentorMistakeKind = "concept_gap" | "careless_slip" | "time_pressure" | "skipped";

export interface MentorQuestionDiagnosis {
    question_id: string;
    sequence: number;
    subject?: string | null;
    kind: MentorMistakeKind;
    reason: string;
    question_preview: string;
}

export interface MentorSubjectInsight {
    subject: string;
    attempted: number;
    correct: number;
    total: number;
    accuracy: number;
    verdict: "strong" | "developing" | "weak";
}

export interface MentorPlanItem {
    priority: number;
    title: string;
    detail: string;
    subject?: string | null;
}

export interface MentorReport {
    quiz_id: string;
    title: string;
    available: boolean;
    message?: string | null;
    summary?: string | null;
    diagnoses: MentorQuestionDiagnosis[];
    subject_insights: MentorSubjectInsight[];
    plan: MentorPlanItem[];
    strengths: string[];
    focus_flags: string[];
    ai_assisted: boolean;
    generated_at?: string | null;
}

/** What the invite landing page may show: never a question or an answer. */
export interface InviteDetails {
    invite_code: string;
    quiz_id: string;
    coaching_id?: string | null;
    tenant_slug?: string | null;
    title?: string | null;
    question_count: number;
    duration_minutes?: number | null;
    opens_at?: string | null;
    closes_at?: string | null;
    window: "open" | "upcoming" | "closed";
    shared_by: string;
    coaching_name?: string | null;
    visit_count: number;
}

export interface ChallengeRow {
    /** null when the organisation keeps ranks confidential. */
    rank: number | null;
    name: string;
    percent: number;
    correct: number;
    total: number;
    submitted_at?: string | null;
    is_you: boolean;
    is_author: boolean;
    /** A student of another coaching who took it as a guest. */
    is_guest?: boolean;
    guest_from?: string | null;
}

export interface GuestChallengeSitting {
    quiz_id: string;
    title?: string | null;
    duration_minutes: number;
    started_at: string;
    deadline?: string | null;
    server_time: string;
    questions: { question_id: string; text: string; options: string[]; question_type?: string; image_url?: string | null; marks?: number }[];
}

export interface ChallengeView {
    quiz_id: string;
    title?: string | null;
    is_author: boolean;
    link_opens: number;
    joined: number;
    completed: number;
    average_percent: number | null;
    leaderboard: ChallengeRow[];
    /** The organisation keeps ranks confidential: only your own row is sent. */
    ranks_hidden?: boolean;
}

export interface InviteLink {
    invite_code: string;
    quiz_id: string;
    channel: string;
    share_url: string;
    visit_count: number;
    last_visited_at?: string | null;
}

export interface AIGeneratedQuestion {
    text: string;
    options: string[];
    correct_option: number;
    explanation?: string | null;
    subject?: string | null;
    source_year?: number | null;
}

export interface AILegacyBuilderResult {
    run_id: string;
    status: string;
    message: string;
    draft_quiz_id?: string | null;
    question_count: number;
    questions: AIGeneratedQuestion[];
}

export interface AILegacyBuilderPayload {
    exam_id: string;
    coaching_id?: string | null;
    year_start: number;
    year_end: number;
    sources?: string[];
    question_count?: number;
    subject_hint?: string | null;
}

export interface SubjectMastery {
    subject: string;
    mastery: number;
    uncertainty: number;
    attempted: number;
    total: number;
}

export interface AtRiskStudent {
    student_id: string;
    full_name?: string | null;
    email?: string | null;
    attempts: number;
    average_score: number;
    last_attempt_at?: string | null;
    days_inactive?: number | null;
    reasons: string[];
}

export interface QuizPerformance {
    quiz_id: string;
    title: string;
    status: string;
    participants: number;
    average_score: number;
    pass_rate: number;
    go_live_at?: string | null;
}

export interface AdminAnalytics {
    coaching_id: string;
    generated_at: string;
    total_students: number;
    active_students: number;
    participation_rate: number;
    attempts_in_window: number;
    average_score: number;
    window_days: number;
    subject_mastery: SubjectMastery[];
    at_risk_students: AtRiskStudent[];
    quiz_performance: QuizPerformance[];
}

export interface ExamBenchmark {
    exam_id: string;
    exam_name: string;
    your_average: number;
    your_participants: number;
    cohort_size: number;
    cohort_average: number;
    percentile: number;
}

export interface BenchmarkReport {
    coaching_id: string;
    generated_at: string;
    opted_in: boolean;
    minimum_cohort_size: number;
    benchmarks: ExamBenchmark[];
    suppressed_exams: string[];
    message?: string | null;
}

export type NotificationKind =
    | "quiz_live"
    | "result_published"
    | "explanation_reviewed"
    | "paper_approved"
    | "account"
    | "system";

export interface NotificationPublic {
    notification_id: string;
    kind: NotificationKind | string;
    title: string;
    body: string;
    link?: string | null;
    read_at?: string | null;
    created_at?: string | null;
}

export interface NotificationFeed {
    unread_count: number;
    items: NotificationPublic[];
}

/**
 * `answer_language` value meaning "answer in whatever language the question was
 * asked in".
 *
 * Sent instead of the interface language, which is what shipped and which meant
 * a student on the default English interface got English answers to questions
 * they typed or spoke in Hindi. The server reads the script of the question (and
 * Whisper's guess, for spoken ones) to decide; a student who explicitly picks a
 * language still overrides it.
 */
export const ANSWER_LANGUAGE_AUTO = "auto";

export interface TranscriptionPublic {
    text: string;
    detected_language?: string | null;
    language_probability?: number | null;
    duration_seconds?: number | null;
    /** Set when transcription could not run at all; show it and fall back to typing. */
    error?: string | null;
    /** False when the server has no speech model configured. */
    available: boolean;
    /** Mean per-token log probability: about -0.1 when certain, -1.5 when guessing. */
    confidence?: number | null;
    /** The recogniser was unsure enough that the words deserve a second look.
     *  A prompt to check, never a block on sending — the student can read their
     *  own question and judges it better than a threshold does. */
    low_confidence?: boolean;
}

export interface LanguageInfo {
    code: string;
    label: string;
    native_label: string;
    bcp47: string;
    rtl: boolean;
}

export interface YouTubeRecommendation {
    status: "ready" | "disabled" | "not_applicable" | "unavailable" | "no_match";
    video?: {
        video_id: string;
        title: string;
        channel: string;
        published_at: string;
        views: number;
        duration_seconds: number;
        checked_at: string;
    } | null;
    /** Why it is offered: "asked" | "struggling" | "visual". */
    reason?: string | null;
    /** False for a student who may be under 18: link out, never play inline. */
    embed_allowed?: boolean;
}

export interface DoubtPublic {
    /** The chat's name (first turn only): a short topic title. */
    conversation_title?: string | null;
    /** "prose" | "worked" | "howto" | "diagram" - decides the follow-up buttons. */
    answer_shape?: string | null;
    video_suggestion_eligible?: boolean;
    /** Why a video is offered under this answer, decided when it was saved. */
    video_reason?: string | null;
    /** How this answer is best laid out: "text" | "steps" | "diagram". */
    presentation_mode?: string | null;
    /** Asked in teacher mode; used to reopen a conversation in that mode. */
    teacher_mode?: boolean;
    /** The student's own thumbs up / down on this answer. */
    rating?: "up" | "down" | null;
    /** The free local model answered this turn (paid AI off, budget, or fallback). */
    answered_free?: boolean;

    doubt_id: string;
    /** Thread this turn belongs to. Send it back on the next question to ask a
     *  follow-up; the server replays the earlier turns into the prompt. Always
     *  populated — a first question is a thread of one — so grouping needs no
     *  special case for doubts asked before threading existed. */
    conversation_id?: string | null;
    /** 0 for the opening question, 1 for the first follow-up, and so on. */
    turn_index?: number;
    subject?: string | null;
    question_text: string;
    image_uri?: string | null;
    audio_uri?: string | null;
    voice_asked?: boolean;
    detected_language?: string | null;
    answer_language?: string | null;
    answer_text: string;
    ai_assisted: boolean;
    vision_assisted?: boolean;
    asked_at: string;
    /** Which subject-area expert answered. Shown to the student so an
     *  auto-routing mistake is visible and one click from fixed — a router that
     *  decides silently is indistinguishable from a bad model. */
    routed_category?: SubjectCategory | null;
    /** The model that wrote this answer, e.g. "qwen2.5:7b" or
     *  "claude-haiku-4-5". Stored per answer, so the badge survives a reload —
     *  the streaming meta event alone made it vanish on the next page load. */
    answered_by_model?: string | null;
    routing_confidence?: number | null;
    /** The terms that triggered the routing, e.g. ["writ petition"]. */
    routing_evidence?: string[];
    /** text | subject | profile | override | default */
    routing_source?: string | null;
    /** Documents a chapter-scoped answer was drawn from. Empty for an
     *  ordinary question. */
    grounded_sources?: string[];
    /** Where in those documents the answer came from. */
    grounded_citations?: Array<{ source_id: string; filename: string; page?: number | null }>;
    /** The material scope this answer was asked under; absent for older turns. */
    scope_mode?: "smart" | "own" | "library" | "general" | null;
}

/** Which subject-area expert the router would pick for a question, previewed
 *  before any AI call is spent. */
export interface DomainDetection {
    category: SubjectCategory;
    label: string;
    confidence: number;
    /** Terms that drove the decision, e.g. ["writ petition"]. */
    evidence: string[];
    /** text | subject | profile | override | default */
    source: string;
    /** False when evidence is thin — present it as a suggestion, not a verdict. */
    confident: boolean;
}

export interface TelemetryStudentInsight {
    student_id: string;
    risk_score: number;
    risk_level: "low" | "medium" | "high";
    recommended_action: string;
    event_counts: Record<string, number>;
    last_event_at?: string | null;
}

export interface TelemetryInsights {
    quiz_id: string;
    total_events: number;
    event_totals: Record<string, number>;
    students: TelemetryStudentInsight[];
    high_risk_count: number;
}


export interface ExplanationSubmission {
    submission_id: string;
    quiz_id: string;
    question_id: string;
    coaching_id: string;
    student_id: string;
    tenant_slug: string;
    body: string;
    status: string;
    reviewer_id?: string | null;
    reviewer_notes?: string | null;
    reviewed_at?: string | null;
}

export interface TelemetrySnapshot {
    events: any[];
    summary: Array<{ event_type: string; count: number; last_seen_at?: string | null }>;
}

/** The academic area a subject belongs to. Single-valued and authoritative —
 *  this is the axis every picker narrows by first, so a user choosing
 *  "Law" sees 24 subjects instead of scrolling all 647. */
export type SubjectCategory =
    | "school"
    | "engineering"
    | "computer_it"
    | "medical"
    | "commerce_management"
    | "science"
    | "law"
    | "arts_humanities"
    | "education"
    | "languages"
    | "competitive"
    | "vocational"
    | "research"
    | "other";

/** How far along a learner is. Orthogonal to category — "Mathematics" exists at
 *  school, UG and PG, and a Class 9 student must not be offered M.Sc. papers. */
export type EducationLevel =
    | "primary"
    | "middle"
    | "secondary"
    | "senior_secondary"
    | "undergraduate"
    | "postgraduate"
    | "diploma"
    | "competitive"
    | "skill"
    | "any";

/** One row of the area picker. `subject_count` is computed server-side so the
 *  client never has to load the whole catalogue just to show the counts. */
export interface SubjectCategoryInfo {
    category: SubjectCategory;
    label: string;
    description: string;
    icon: string;
    subject_count: number;
    levels: EducationLevel[];
}

export interface Subject {
    subject_id: string;
    name: string;
    description?: string | null;
    tags: string[];
    category: SubjectCategory;
    education_level: EducationLevel;
    difficulty: string;
    weightage: number;
    ordering: number;
    exam_ids: string[];
}

export type AIProviderKind = "ollama" | "openai" | "anthropic" | "deepseek" | "huggingface";
export type AIModelScope = "platform" | "coaching" | "user";

/** What a model is good at. Coarse on purpose — genuine capability differences
 *  between free open models exist only for these few things, so the 13 subject
 *  categories map many-to-one onto them (see `/ai-models/routing`). */
export type ModelSpecialisation = "general" | "code" | "math" | "medical" | "vision";

/** One routing row: which subject areas a specialisation actually serves. */
export interface SpecialisationInfo {
    specialisation: ModelSpecialisation;
    label: string;
    description: string;
    categories: string[];
    suggested_free_model?: string | null;
}

/** What a chat message was read as asking for.
 *
 *  `intent` is "ask" (answer the question) or "create" (build a paper).
 *  `missing` lists what the student still has to supply — only ever ["topic"]
 *  today, because every other generation field has a usable default. */
export interface ChatIntentResponse {
    intent: "ask" | "create" | "artefact";
    reasons: string[];
    topic?: string | null;
    count?: number | null;
    difficulty?: string | null;
    kind?: "quiz" | "assignment" | null;
    missing: string[];
    /** Set only when `intent` is "artefact" — which study artefact was asked
     *  for. A quiz request takes the paper-builder path instead. */
    artefact_kind?: ArtefactKind | null;
}

/** A named group of documents — "Module 4", "Polity revision".
 *
 *  `source_count` is counted live by the server rather than read from a stored
 *  field, because a chapter claiming "3 documents" while holding none is the
 *  sort of small lie that costs trust in everything else on the screen. */
export interface NotebookPublic {
    notebook_id: string;
    title: string;
    description?: string | null;
    source_count: number;
    created_at?: string | null;
}

/** A study artefact: the outline, and the rendered result once it exists.
 *
 *  `sections` is the OUTLINE — the editable checkpoint. `content` is the
 *  rendered payload and its shape depends on `kind`, which is why it is typed
 *  as unknown here and narrowed by each renderer. Typing it as a union would
 *  make every consumer handle six shapes it does not care about. */
export type ArtefactKind =
    | "notes"
    | "flashcards"
    | "diagram"
    | "infographic"
    | "deck"
    | "document"
    | "video";

export type ArtefactStatus = "planning" | "planned" | "rendering" | "ready" | "failed";

export type AnimationLayout = "list" | "flow" | "compare" | "cycle";

export interface ArtefactPlanSection {
    section_id: string;
    heading: string;
    points: string[];
    layout?: AnimationLayout | null;
}

/** A section as the editor submits it. `section_id` is absent for one the
 *  author just added — the server mints it, so the client cannot create a
 *  duplicate id. */
export interface ArtefactPlanSectionInput {
    section_id?: string;
    heading: string;
    points: string[];
    layout?: AnimationLayout | null;
}

export interface ArtefactPlanRequest {
    reference_doubt_id?: string;
    /** Display title when it differs from the topic (a revision: "X (updated)"). */
    title?: string;
    /** What the student typed - shown as their message when the chat reopens. */
    request_text?: string;
    /** The chat it is made in, and the turn it follows - so it reappears there. */
    conversation_id?: string | null;
    after_doubt_id?: string | null;
    /** Skip the outline draft and build immediately — see the backend model. */
    skip_outline?: boolean;
    kind: ArtefactKind;
    topic: string;
    subject?: string | null;
    notebook_id?: string | null;
    source_id?: string | null;
    language?: string | null;
    section_count?: number;
}

export interface ArtefactPublic {
    research_project_id?: string | null;
    request_text?: string | null;
    conversation_id?: string | null;
    after_doubt_id?: string | null;
    video_style?: string | null;
    research_references?: { source_id: string; title: string; url: string }[];
    artefact_id: string;
    kind: ArtefactKind;
    title: string;
    topic?: string | null;
    subject?: string | null;
    language: string;
    status: ArtefactStatus;
    status_detail?: string | null;
    sections: ArtefactPlanSection[];
    grounded_sources: string[];
    content?: Record<string, unknown> | null;
    /** Is there a file to download for this artefact?
     *
     *  THE SERVER DECIDES. This replaced a hardcoded
     *  `FILE_KINDS = new Set(["deck", "document", "notes"])` in the component,
     *  which was a second copy of the server's `EXPORTERS` map — and the two
     *  drifted the moment flashcards and infographic gained exporters: they
     *  worked, and no button rendered for them. It also accounts for whether
     *  the artefact has finished rendering, which a set of kinds never could. */
    exportable?: boolean;
    notebook_id?: string | null;
    created_at?: string | null;
}

/** One uploaded document and how its processing is going.
 *
 *  `status` walks uploaded -> parsing -> chunking -> contextualising ->
 *  embedding -> ready, or lands on failed/quarantined. `status_detail` carries
 *  the reason for the last two, and is written to be shown verbatim. */
export interface SourcePublic {
    source_id: string;
    filename: string;
    material_type?: string;
    language?: string | null;
    exam_tags?: string[];
    edition_label?: string | null;
    work_title?: string | null;
    supersedes_source_id?: string | null;
    rights_basis?: "own_work" | "third_party" | "licensed" | "public_domain";
    license_type?: string | null;
    license_reference?: string | null;
    attribution_text?: string | null;
    publication_scope?: "private" | "coaching" | "platform";
    issued_at?: string | null;
    expires_at?: string | null;
    status: string;
    visibility?: string;
    review_state?: string;
    status_detail?: string | null;
    page_count: number;
    chunk_count: number;
    size_bytes: number;
    notebook_id?: string | null;
    created_at?: string | null;
    ingested_at?: string | null;
    warnings?: string[];
}

export interface LibraryItemPublic {
    source_id: string;
    filename: string;
    material_type?: string;
    language?: string | null;
    exam_tags?: string[];
    /** A subject category (science, engineering, ...), or absent when unclassified. */
    subject?: string | null;
    edition_label?: string | null;
    work_title?: string | null;
    supersedes_source_id?: string | null;
    attribution_text?: string | null;
    license_type?: string | null;
    issued_at?: string | null;
    expires_at?: string | null;
    status: string;
    visibility?: string;
    review_state?: string;
    page_count: number;
    chunk_count: number;
    size_bytes: number;
    created_at?: string | null;
    ingested_at?: string | null;
    /** Which shelf this sits on for the viewer: published everywhere, approved
     *  by their own coaching, or their own upload. */
    shelf?: "platform" | "coaching" | "mine" | "class";
    /** The class sections it is shared with (plan 5d.2). */
    section_ids?: string[];
    /** Where this viewer stopped — kept on the server, so it follows them
     *  between devices. */
    last_offset?: number;
    last_page?: number | null;
    progress_percent?: number;
    last_read_at?: string | null;
}

export interface ReadingProgressPublic {
    source_id: string;
    offset: number;
    page?: number | null;
    percent: number;
    updated_at?: string | null;
}

export interface GuideTopic { title: string; ordinal?: number | null; page?: number | null }
export interface SourceGuidePublic {
    source_id: string;
    summary: string;
    topics: GuideTopic[];
    /** "headings" = the document's own (free); "ai" = written on request. */
    topics_from: "headings" | "ai" | "none";
    generated_at?: string | null;
}

/** One card on the "My notes" dashboard. */
export interface MyNotesCard {
    target_type: AnnotationTargetType;
    target_id: string;
    title: string;
    subtitle: string;
    total: number;
    highlights: number;
    notes: number;
    updated_at?: string | null;
    /** A library item that is not the student's own upload (shown with a globe). */
    shared: boolean;
}

export type HighlightColor = "yellow" | "green" | "blue" | "pink";

/** A student's highlight or note. `start`/`end` are offsets into the section's text. */
export type AnnotationTargetType = "library_source" | "research_project";
/** What notes hang on: a library document, or a research project. */
export interface AnnotationTarget { targetId: string; targetType: AnnotationTargetType }

export interface AnnotationPublic {
    annotation_id: string;
    target_type: AnnotationTargetType;
    target_id: string;
    kind: "highlight" | "note";
    chunk_id?: string | null;
    ordinal?: number | null;
    page?: number | null;
    start?: number | null;
    end?: number | null;
    quote: string;
    color: HighlightColor;
    note: string;
    /** Where it came from in words ("Source: ...", "Report: ..."), for research clips. */
    label?: string;
    created_at?: string | null;
}

export type AnnotationCreate = Omit<AnnotationPublic, "annotation_id" | "kind" | "created_at" | "target_type"> & { target_type?: AnnotationTargetType };

export interface LibraryPagePublic {
    source_id: string;
    chunk_id: string;
    ordinal: number;
    page?: number | null;
    heading?: string | null;
    text: string;
    ocr_confidence?: number | null;
    warnings?: string[];
    preview_url?: string | null;
}

export interface EditionComparisonPublic {
    source_id: string;
    previous_source_id: string;
    current_label?: string | null;
    previous_label?: string | null;
    added: number;
    removed: number;
    changed: number;
    unchanged: number;
    truncated: boolean;
    sections: Array<{
        status: "added" | "removed" | "changed" | "unchanged";
        ordinal: number;
        previous_page?: number | null;
        current_page?: number | null;
        previous_text?: string | null;
        current_text?: string | null;
        similarity_percent: number;
    }>;
}

export interface ScopePracticeRequest {
    mode: "smart" | "own" | "library";
    source_ids?: string[];
    subject?: string | null;
    topic?: string | null;
    count?: 10 | 20 | 30;
}

export interface SourcePracticeDraftPublic {
    source_id: string;
    source_filename: string;
    quiz_id?: string | null;
    total_questions: number;
    warnings: string[];
    questions: QuizQuestion[];
}

export interface ContributionRewardPublic {
    source_id: string;
    points: number;
    status: string;
}

export interface SourceReportPublic {
    report_id: string;
    source_id: string;
    reason: string;
    status: string;
    created_at?: string | null;
}

/** Answer-cache savings for one coaching.
 *
 *  `hits` is the number of questions answered WITHOUT calling a model — the
 *  saving, in requests. Reported rather than predicted, because how often
 *  students phrase the same question identically is a property of a real cohort
 *  and cannot be guessed in advance. */
/** What `POST /students/import` reports back.
 *
 * `created` carries a one-time temporary password per student. It exists
 * because this deployment has no email delivery, so the admin is the only
 * route by which a new student can learn how to sign in. It is returned once
 * and stored nowhere readable -- the user row keeps a hash.
 */
/** An announcement from the coaching.
 *
 * Typed rather than `any[]`, which is what hid a missing `created_at` long
 * enough for the dashboard to render a blank date beside every notice
 * (usp#57). A shape the compiler can check is the fix for that class of bug,
 * not just for this one field.
 */
/** One person's AI usage this month, from `GET /ai/usage/by-actor`.
 *
 * `approx_tokens` is chars/4 and always present; `input_tokens` and
 * `output_tokens` are what the provider actually billed and are null where it
 * did not report them. They are separate fields on purpose -- adding a measured
 * number to an estimated one produces a total that is neither.
 */
/** This coaching's AI spend against its monthly ceiling, from `/ai/budget`.
 *
 * Readable by anyone in the coaching on purpose -- it does NOT attribute. The
 * per-person view is `ActorSpendReport`, behind an admin-only endpoint.
 */
/** One student's topic-wise mastery, for their coaching's admins.
 *
 * A separate endpoint from `/dashboard/heatmap`, which is scoped to the caller
 * on purpose -- see the note on `student_heatmap` in the backend. This one is
 * admin-only and checks the student belongs to the caller's coaching.
 */
export interface StudentHeatmap {
    student_id: string;
    full_name?: string | null;
    heatmap: HeatmapPoint[];
}

export interface AIBudgetSummary {
    approx_tokens_used: number;
    monthly_cap: number;
    unlimited: boolean;
    percent_used?: number;
}

export interface ActorSpend {
    actor_id: string;
    full_name: string;
    email?: string | null;
    role?: string | null;
    calls: number;
    approx_tokens: number;
    input_tokens?: number | null;
    output_tokens?: number | null;
    cost_usd?: number | null;
    failures: number;
    last_call_at?: string | null;
}

export interface ActorSpendReport {
    scope: string;
    since?: string;
    actors: ActorSpend[];
    error?: string;
}

export interface Notice {
    notice_id: string;
    title: string;
    body: string;
    category: string;
    pinned: boolean;
    expires_at?: string | null;
    language?: string | null;
    created_at?: string | null;
}

/** What the composer sends. Matches `NoticeCreate` on the server. */
export interface NoticeCreateRequest {
    title: string;
    body: string;
    category: string;
    pinned?: boolean;
    expires_at?: string | null;
    language?: string;
}

export interface StudentImportCreated {
    row: number;
    email: string;
    full_name: string;
    temporary_password: string;
    /** "9-A" when a school roster placed the student; null otherwise. */
    class_section?: string | null;
}

export interface StudentImportFailure {
    row: number;
    email: string;
    message: string;
}

export interface StudentImportResult {
    imported: number;
    failed: number;
    created: StudentImportCreated[];
    errors: StudentImportFailure[];
}

export interface AnswerCacheStats {
    entries: number;
    hits: number;
    scope: string;
    error?: string;
}

/** Safe projection of a model config. Never carries key material — the server
 *  sends only `has_api_key` plus a masked `api_key_hint`. */
export interface AIModelOverviewItem {
    config_id: string;
    label: string;
    provider: string;
    model_name: string;
    is_active: boolean;
    is_default: boolean;
    is_free: boolean;
    scope?: AIModelScope;
    admin_status?: AIModelAdminStatus;
    added_by?: string | null;
    coaching_name?: string | null;
    created_at?: string | null;
    calls: number;
    tokens: number;
    cost_usd: number;
}

export interface AIModelOverviewSection {
    key: "admin" | "coachings" | "students";
    items: AIModelOverviewItem[];
    stats: { total: number; active: number; paid: number; calls: number; tokens: number; cost_usd: number };
}

export interface AIModelOverview {
    since: string;
    sections: AIModelOverviewSection[];
}

/** Platform moderation of a coaching's or a student's connection. */
export type AIModelAdminStatus = "active" | "paused" | "blocked";

export interface AIModelConfigPublic {
    config_id: string;
    label: string;
    provider: AIProviderKind;
    model_name: string;
    base_url?: string | null;
    scope: AIModelScope;
    coaching_id?: string | null;
    owner_user_id?: string | null;
    is_active: boolean;
    is_default: boolean;
    /** Coaching models only: the coaching lets its students use it. */
    share_with_students?: boolean;
    specialisation: ModelSpecialisation;
    supports_vision: boolean;
    generates_images?: boolean;
    notes?: string | null;
    has_api_key: boolean;
    api_key_hint?: string | null;
    can_manage: boolean;
    /** Super admin viewing a coaching's or a student's connection. */
    can_moderate?: boolean;
    admin_status?: AIModelAdminStatus;
    moderation_reason?: string | null;
    created_by_name?: string | null;
    /** Created by the free-defaults seeder rather than by a person. */
    is_seeded: boolean;
    /** True when this model costs nothing to run (local Ollama, or a free tier). */
    is_free: boolean;
    /**
     * The specialist model this row wanted, set only when it is running a
     * substitute because the preferred one is not pulled locally. Present means
     * routing to this specialisation is nominal, not real — the card says so.
     */
    preferred_model?: string | null;
}

export interface AIModelConfigCreatePayload {
    label: string;
    provider: AIProviderKind;
    model_name: string;
    base_url?: string;
    /** Anthropic only. Sent as the `anthropic-workspace-id` header; a key that
     *  is not workspace-scoped fails every call without it. */
    workspace_id?: string;
    api_key?: string;
    scope: AIModelScope;
    coaching_id?: string;
    is_active?: boolean;
    is_default?: boolean;
    share_with_students?: boolean;
    specialisation?: ModelSpecialisation;
    supports_vision?: boolean;
    generates_images?: boolean;
    notes?: string;
}

export interface AIModelConfigUpdatePayload {
    /** Anthropic only; "" clears it. */
    workspace_id?: string;
    label?: string;
    model_name?: string;
    base_url?: string;
    /** omit = keep stored key; "" = clear it */
    api_key?: string;
    is_active?: boolean;
    share_with_students?: boolean;
    specialisation?: ModelSpecialisation;
    supports_vision?: boolean;
    generates_images?: boolean;
    notes?: string;
}

/** One model a person may pick in chat. */
export interface ChatModelOption {
    config_id: string;
    label: string;
    model_name: string;
    provider: string;
    /** "own" - they added it; "coaching" - their coaching shares it. */
    source: "own" | "coaching";
    is_free: boolean;
}

export interface ChatModelChoices {
    options: ChatModelOption[];
    automatic_config_id?: string | null;
    automatic_label?: string | null;
    coaching_shares: boolean;
}

export interface AIModelTestResult {
    ok: boolean;
    detail: string;
    latency_ms?: number | null;
    sample?: string | null;
}

export interface ProviderFieldSpec {
    name: string;
    label: string;
    required: boolean;
    placeholder?: string | null;
    helper?: string | null;
    secret: boolean;
    /** Where to go to find this value. Rendered as a link beside the input —
     *  a helper sentence can only describe a value, not tell you where it lives. */
    help_url?: string | null;
    help_label?: string | null;
}

export interface ModelDiscoveryRequest {
    provider: AIProviderKind;
    api_key?: string;
    base_url?: string;
    workspace_id?: string;
}

export interface ModelDiscoveryResult {
    ok: boolean;
    models: string[];
    message?: string | null;
}

export interface ProviderCatalogEntry {
    provider: AIProviderKind;
    label: string;
    is_free: boolean;
    requires_api_key: boolean;
    supports_vision: boolean;
    generates_images?: boolean;
    docs_url?: string | null;
    signup_url?: string | null;
    suggested_models: string[];
    fields: ProviderFieldSpec[];
    description: string;
}

export interface SubjectPayload {
    name: string;
    description?: string;
    tags?: string[];
    category?: SubjectCategory;
    education_level?: EducationLevel;
    difficulty?: string;
    weightage?: number;
    ordering?: number;
}

/** Server-side narrowing for the subject list. Omit both to get everything. */
export interface SubjectListFilter {
    category?: SubjectCategory;
    education_level?: EducationLevel;
    /** Ask for the picker shape: subject_id, name, category, education_level
     *  and difficulty only.
     *
     *  The catalogue is 647 rows, and the full shape is 164 KB — of which
     *  description, tags, exam_ids, weightage and ordering are 44% and are
     *  rendered by nothing except the Manage Subjects editor. Every other
     *  caller is a dropdown. */
    lite?: boolean;
}

/** What the user told us about themselves in the post-signup onboarding step.
 *  Every field is optional — onboarding is skippable, and a partly-answered
 *  profile is still worth having. */
export interface LearnerProfile {
    education_level?: EducationLevel | null;
    category?: SubjectCategory | null;
    /** Free text: "Class 10", "B.Tech CSE", "UPSC CSE". */
    class_or_course?: string | null;
    subject_ids: string[];
    /** Board or medium: "CBSE", "UP Board", "Hindi medium". */
    board?: string | null;
    /** True once finished. `skipped` is tracked separately so a deliberate
     *  skip isn't re-prompted forever but is still distinguishable from a
     *  genuine completion in analytics. */
    completed: boolean;
    skipped: boolean;
    completed_at?: string | null;
}

/** Partial write — the modal saves after each step, not only at the end. */
export type LearnerProfileUpdate = Partial<Omit<LearnerProfile, "completed_at">>;

export interface MindfulNudge {
    tone: "celebrate" | "momentum" | "reset";
    headline: string;
    message: string;
    cta_label: string;
    cta_path: string;
    subtext?: string | null;
}

export interface LearningSubject {
    category: string;
    answers: number;
    taught: number;
    checks_asked: number;
    correct: number;
    partial: number;
    incorrect: number;
}

/** Phase E — the student's own learning from the AI tutor. */
export interface LearningSummary {
    days: number;
    answers: number;
    taught: number;
    checks_asked: number;
    checks_answered: number;
    correct: number;
    partial: number;
    incorrect: number;
    accuracy: number | null;
    subjects: LearningSubject[];
}

export interface StreakSnapshot {
    current: number;
    best: number;
    active_days_this_week: number;
    weekly_goal: number;
    progress: number;
    days_since_last_attempt?: number | null;
    nudge: MindfulNudge;
}

/** One subject's row on the Confidence Heatmap (USP #1).
 *
 *  `mastery` and `uncertainty` are both 0-1 fractions, not percentages — the
 *  chart's YAxis multiplies by 100 for display. */
export interface HeatmapPoint {
    subject: string;
    mastery: number;
    uncertainty: number;
}

export interface NotFoundEventLog {
    event_id: string;
    tenant_slug: string;
    path: string;
    referrer?: string | null;
    user_id: string;
    user_role: string;
    user_agent?: string | null;
    occurred_at: string;
}

export type StudentStatus = "active" | "suspended" | "alumni" | "pending_approval" | "rejected";

export interface EnrollmentHistoryEntry {
    from_status: string;
    to_status: string;
    changed_at: string;
    changed_by?: string | null;
    reason?: string | null;
}

export interface UserPublic {
    id: string;
    email: string;
    full_name: string;
    role: UserRole;
    coaching_id?: string | null;
    mobile?: string | null;
    city?: string | null;
    pincode?: string | null;
    expert_subject?: string | null;
    tenant_slug: string;
    preferred_language?: string | null;
    is_active?: boolean;
    student_status?: StudentStatus | null;
    enrollment_history?: EnrollmentHistoryEntry[];
    permissions?: string[];
    must_change_password?: boolean;
    guardian_name?: string | null;
    guardian_contact?: string | null;
    feature_access?: Record<string, boolean>;
}

/** A school's class section for the current academic year (plan step 3). */
export interface ClassSection {
    id: string;
    coaching_id: string;
    academic_year: string;
    grade: number;
    section: string;
    class_teacher_id?: string | null;
    teacher_ids: string[];
    student_count: number;
}

/** What this account may use (plan §5c item 3). `restricted: false` means the
 *  school catalogue does not govern it (admins, coaching accounts). */
export interface MyAccess {
    restricted: boolean;
    features: Record<string, boolean>;
}

export interface AccessFeature {
    key: string;
    label: string;
    enabled: boolean;
}

export interface RoleAccessDefaults {
    role: "teacher" | "student";
    features: AccessFeature[];
}

/** Work set for sections (plan §5c item 3b). */
export interface Assignment {
    id: string;
    title: string;
    instructions?: string | null;
    kind: "paper" | "task";
    quiz_id?: string | null;
    quiz_title?: string | null;
    section_ids: string[];
    section_labels: string[];
    due_at?: string | null;
    created_by_id: string;
    created_by_name: string;
    created_at: string;
    assigned_count: number;
    done_count: number;
}

export interface AssignmentPayload {
    title: string;
    instructions?: string;
    kind: "paper" | "task";
    quiz_id?: string;
    section_ids: string[];
    due_at?: string;
}

export interface AssignmentStudentStatus {
    student_id: string;
    full_name: string;
    section: string;
    status: "done" | "pending" | "late";
    done_at?: string | null;
    note?: string | null;
    ai_use?: string[];
    score?: number | null;
}

export interface MyWorkItem {
    id: string;
    title: string;
    instructions?: string | null;
    kind: "paper" | "task";
    quiz_id?: string | null;
    quiz_title?: string | null;
    due_at?: string | null;
    set_by: string;
    status: "done" | "pending" | "late";
    done_at?: string | null;
    note?: string | null;
    ai_use?: string[];
    score?: number | null;
}

/** Reports per level (plan §5c item 4). */
export interface StudentActivity {
    student_id: string;
    full_name: string;
    section: string;
    last_active?: string | null;
    papers_30d: number;
    avg_percent?: number | null;
    doubts_30d: number;
    reading_30d: number;
    work_done: number;
    work_total: number;
}

export interface SectionSummary {
    section_id: string;
    section: string;
    students: number;
    active_7d: number;
    papers_30d: number;
    avg_percent?: number | null;
    work_set: number;
    completion_percent?: number | null;
}

export interface TeacherSummary {
    teacher_id: string;
    full_name: string;
    sections: string[];
    students: number;
    work_set_30d: number;
    completion_percent?: number | null;
    last_active?: string | null;
    is_active: boolean;
}

export interface ClassReport {
    window_days: number;
    sections: SectionSummary[];
    students: StudentActivity[];
}

export interface SchoolReport extends ClassReport {
    totals: Record<string, number>;
    teachers: TeacherSummary[];
}

export interface OrganisationSummary {
    coaching_id: string;
    name: string;
    org_type: "coaching" | "school";
    status: string;
    admins: number;
    teachers: number;
    students: number;
    active_students_7d: number;
    papers_30d: number;
    doubts_30d: number;
    work_set_30d: number;
    admin_actions_30d: number;
    last_active?: string | null;
}

export type AcademicYearStatus = "upcoming" | "current" | "closed";

/** One session of a school (plan §5c). Exactly one is current. */
export interface AcademicYear {
    year: string;
    starts_on: string;
    ends_on: string;
    status: AcademicYearStatus;
    section_count: number;
    student_count: number;
}

export interface RolloverPayload {
    from_year: string;
    copy_sections: boolean;
    promote_students: boolean;
    copy_teachers: boolean;
}

export interface RolloverResult {
    sections_created: number;
    promoted: number;
    graduated: number;
    already_placed: number;
}

export interface ClassSectionPayload {
    /** Defaults to the current year on the server. */
    academic_year?: string;
    grade: number;
    section: string;
    class_teacher_id?: string | null;
    teacher_ids: string[];
}

export interface SectionStudent {
    student_id: string;
    full_name: string;
    email: string;
    roll_no?: string | null;
    is_active: boolean;
}

export interface UserCreatePayload {
    email: string;
    password: string;
    full_name: string;
    role: UserRole;
    coaching_id?: string | null;
    mobile?: string | null;
    city?: string | null;
    pincode?: string | null;
    expert_subject?: string | null;
    tenant_slug: string;
    feature_access?: Record<string, boolean>;
}

export interface UserUpdatePayload {
    full_name?: string;
    role?: UserRole;
    coaching_id?: string | null;
    is_active?: boolean;
    mobile?: string | null;
    city?: string | null;
    pincode?: string | null;
    expert_subject?: string | null;
    permissions?: string[];
    feature_access?: Record<string, boolean>;
}

export interface QuizAIGenerateRequest {
    subject: string;
    topic?: string;
    exam_category?: string;
    grade_level?: string;
    difficulty?: string;
    count?: number;
    language?: string;
    /** Configured AI model to use; omit to use the caller's resolved default. */
    model_config_id?: string;
}

export interface StudentAuthoredQuizOverview {
    quiz_id: string;
    title: string;
    status: string;
    coaching_id: string;
    exam_category?: string | null;
    grade_level?: string | null;
    created_by_id?: string | null;
    created_by_name?: string | null;
    question_count: number;
    attempts_taken: number;
    distinct_students: number;
    approved_by_name?: string | null;
    created_at?: string | null;
}

export interface UserListQuery {
    role?: UserRole;
    coaching_id?: string;
    is_active?: boolean;
}

export interface CoachingPublic {
    coaching_id: string;
    name: string;
    owner_name: string;
    contact_email: string;
    contact_phone?: string | null;
    status: string;
    timezone: string;
    logo_url?: string | null;
    address_line1?: string | null;
    address_line2?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    pincode?: string | null;
    benchmarking_enabled?: boolean;
    /** Missing on rows written before schools existed: read as "coaching". */
    org_type?: OrgType;
    board?: string | null;
    affiliation_no?: string | null;
    udise_code?: string | null;
    grades_offered?: number[] | null;
    current_academic_year?: string | null;
}

export type OrgType = "coaching" | "school";

/** POST /coaching/with-admin: an organisation and its first admin in one
 *  request. The admin's role follows `org_type` on the server. */
export interface OrganisationCreatePayload {
    organisation: CoachingCreatePayload & {
        org_type: OrgType;
        board?: string;
        affiliation_no?: string;
        udise_code?: string;
        grades_offered?: number[];
        current_academic_year?: string;
    };
    admin: { email: string; password: string; full_name: string; mobile: string };
}

export interface OrganisationCreated {
    organisation: CoachingPublic;
    admin: UserPublic;
}

export interface CoachingDirectoryEntry {
    coaching_id: string;
    name: string;
    /** A school's students also pick their class on the form. */
    org_type?: "coaching" | "school";
    logo_url?: string | null;
    city?: string | null;
    state?: string | null;
}

export interface CoachingCreatePayload {
    name: string;
    owner_name: string;
    contact_email: string;
    contact_phone?: string;
    gst_number?: string;
    logo_url?: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    state?: string;
    country?: string;
    pincode?: string;
}

export interface CoachingUpdatePayload {
    name?: string;
    contact_email?: string;
    contact_phone?: string;
    gst_number?: string;
    status?: string;
    timezone?: string;
    logo_url?: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    state?: string;
    country?: string;
    pincode?: string;
    benchmarking_enabled?: boolean;
}


export interface ExamSubjectConfigPayload {
    subject_id: string;
    weightage?: number;
    ordering?: number;
}

export interface ExamPublic {
    exam_id: string;
    coaching_id?: string | null;
    name: string;
    level: string;
    status: string;
    tags: string[];
    default_duration_minutes: number;
    attempt_rules?: string | null;
    allowed_subjects: string[];
    syllabus_outline?: string[];
    ai_presets?: Record<string, string>;
    publish_defaults: Record<string, string>;
    subject_configs: ExamSubjectConfigPayload[];
}

export interface ExamCreatePayload {
    name: string;
    conducting_body: string;
    level: string;
    coaching_id?: string | null;
    default_duration_minutes?: number;
    attempt_rules?: string;
    tags?: string[];
    allowed_subjects?: string[];
    subject_configs?: ExamSubjectConfigPayload[];
    syllabus_outline?: string[];
    ai_presets?: Record<string, string>;
    publish_defaults?: Record<string, string>;
}

export interface StudentRegistrationPayload {
    email: string;
    password: string;
    full_name: string;
    coaching_id: string;
    mobile?: string;
    city?: string;
    pincode?: string;
    expert_subject?: string;
    tenant_slug: string;
    /** Required for a school: the class section the student is in. */
    class_section_id?: string;
}

/** A school's class as the public registration form shows it. */
export interface PublicSection {
    id: string;
    grade: number;
    section: string;
}

/** One self-registration on the Approvals list (plan 5e). */
export interface RegistrationRequest {
    id: string;
    full_name: string;
    email: string;
    mobile?: string | null;
    city?: string | null;
    coaching_id: string;
    organisation: string;
    org_type: "coaching" | "school";
    status: "pending_approval" | "rejected";
    section_id?: string | null;
    section_label?: string | null;
    registered_at?: string | null;
    reason?: string | null;
}

export interface AdminCreatePayload {
    email: string;
    password: string;
    full_name: string;
    coaching_id: string;
    mobile: string;
    role: AdminCreatableRole;
}

/** Query params for a paged list, with empty values omitted. */
const buildListParams = (args?: PagedListArgs | void): Record<string, string | number> => {
    const params: Record<string, string | number> = {
        limit: (args && args.limit) ?? 20,
        offset: (args && args.offset) ?? 0
    };
    if (!args) return params;
    for (const [key, value] of Object.entries(args)) {
        if (key === "limit" || key === "offset") continue;
        if (value !== undefined && value !== null && value !== "") {
            params[key] = value;
        }
    }
    return params;
};

export const api = createApi({
    reducerPath: "api",
    baseQuery: instrumentedBaseQuery,
    tagTypes: [
        "Registrations",
        "Work",
        "Access",
        "Sections",
        "Dashboard",
        "Quiz",
        "Attempts",
        "Notice",
        "Explanation",
        "Students",
        "Subjects",
        "Exams",
        "Users",
        "Coachings",
        "Doubts",
        "Notifications",
        "Artefacts",
        "AIModels",
        "Onboarding", "Translations", "Sources", "Notebooks", "Tenant", "ResearchStudio", "Annotations", "SourceGuide", "Challenge"],
    endpoints: (builder) => ({
        login: builder.mutation<LoginResponse, LoginRequest>({
            query: (body) => ({ url: "/auth/login", method: "POST", body })
        }),
        tenantResolve: builder.query<TenantBranding, string>({
            query: (slug) => ({ url: `/tenant/resolve`, params: { slug } }),
            // Tagged so saving a colour repaints the app immediately. Without
            // this the mutation succeeds, the database is right, and the screen
            // keeps the old palette until a full reload -- which reads as the
            // save having silently failed.
            providesTags: ["Tenant"]
        }),
        logNotFoundEvent: builder.mutation<NotFoundEventLog, { path: string; referrer?: string | null; user_agent?: string }>(
            {
                query: (body) => ({ url: "/events/not-found", method: "POST", body })
            }
        ),
        dashboardOverview: builder.query<any, void>({
            query: () => ({ url: "/dashboard/overview" }),
            providesTags: ["Dashboard"]
        }),
        streaks: builder.query<StreakSnapshot, void>({
            query: () => ({ url: "/dashboard/streaks" }),
            providesTags: ["Dashboard"]
        }),
        /** Phase E: answers, Quick checks and how they went, last 30 days. */
        myLearning: builder.query<LearningSummary, void>({
            query: () => ({ url: "/dashboard/learning" }),
            providesTags: ["Dashboard", "Doubts"]
        }),
        /** The caller's OWN topic-wise mastery — USP #1.
         *
         *  Separate from `dashboardOverview`, whose heatmap is the institute
         *  -wide aggregate and is an admin view: showing it to a student would
         *  present their classmates' averages as their own performance. */
        myHeatmap: builder.query<{ heatmap: HeatmapPoint[] }, void>({
            query: () => ({ url: "/dashboard/heatmap" }),
            providesTags: ["Dashboard"]
        }),
        liveQuizzes: builder.query<QuizSummary[], void>({
            query: () => ({ url: "/quizzes/live" }),
            providesTags: ["Quiz"]
        }),
        /** Study artefacts — decks, diagrams, notes, flashcards.
         *
         *  Two calls on purpose. `planArtefact` is cheap and produces an
         *  outline the author corrects; `renderArtefact` is the expensive one
         *  and only ever runs against an outline a human has agreed with.
         *  Collapsing them would remove the only cheap chance to fix a misread
         *  topic — see docs/artefact-studio-decision.md. */
        planArtefact: builder.mutation<ArtefactPublic, ArtefactPlanRequest>({
            query: (body) => ({ url: "/artefacts/plan", method: "POST", body }),
            invalidatesTags: ["Artefacts"]
        }),
        listArtefacts: builder.query<ArtefactPublic[], string | void>({
            query: (projectId) => ({ url: "/artefacts", params: projectId ? { research_project_id: projectId } : undefined }),
            providesTags: ["Artefacts"]
        }),
        /** Links an artefact that opened a new chat to that chat once it exists. */
        attachArtefact: builder.mutation<ArtefactPublic, { artefactId: string; conversationId: string }>({
            query: ({ artefactId, conversationId }) => ({ url: `/artefacts/${artefactId}/attach`, method: "POST", body: { conversation_id: conversationId } }),
            invalidatesTags: ["Artefacts"]
        }),
        /** Artefacts made in one chat, so reopening it shows them where they were made. */
        listChatArtefacts: builder.query<ArtefactPublic[], string>({
            query: (conversationId) => ({ url: "/artefacts", params: { conversation_id: conversationId } }),
            providesTags: ["Artefacts"]
        }),
        getArtefact: builder.query<ArtefactPublic, string>({
            query: (id) => ({ url: `/artefacts/${id}` }),
            providesTags: (_r, _e, id) => [{ type: "Artefacts" as const, id }]
        }),
        /** Save outline edits. Costs nothing and calls no model — which is the
         *  entire argument for the plan step existing. */
        updateArtefactPlan: builder.mutation<
            ArtefactPublic,
            { artefactId: string; title?: string; sections?: ArtefactPlanSectionInput[] }
        >({
            query: ({ artefactId, ...body }) => ({
                url: `/artefacts/${artefactId}`,
                method: "PATCH",
                body
            }),
            invalidatesTags: (_r, _e, { artefactId }) => [
                { type: "Artefacts" as const, id: artefactId },
                "Artefacts"
            ]
        }),
        renderArtefact: builder.mutation<ArtefactPublic, string>({
            query: (id) => ({ url: `/artefacts/${id}/render`, method: "POST" }),
            invalidatesTags: (_r, _e, id) => [{ type: "Artefacts" as const, id }, "Artefacts"]
        }),
        deleteArtefact: builder.mutation<void, string>({
            query: (id) => ({ url: `/artefacts/${id}`, method: "DELETE" }),
            invalidatesTags: ["Artefacts"]
        }),
        /** Chapters/modules a question can be pointed at. */
        listNotebooks: builder.query<NotebookPublic[], void>({
            query: () => ({ url: "/notebooks" }),
            providesTags: ["Notebooks"]
        }),
        createNotebook: builder.mutation<NotebookPublic, { title: string; description?: string }>({
            query: (body) => ({ url: "/notebooks", method: "POST", body }),
            invalidatesTags: ["Notebooks"]
        }),
        researchPolicy: builder.query<ResearchPolicy, void>({
            query: () => ({ url: "/research-studio/policy" }),
            providesTags: ["ResearchStudio"]
        }),
        updateResearchPolicy: builder.mutation<ResearchPolicy, ResearchPolicy>({
            query: (body) => ({ url: "/research-studio/policy", method: "PUT", body }),
            invalidatesTags: ["ResearchStudio"]
        }),
        /** Every student in this coaching, what applies to them, and whether
         *  it's their own exception or the coaching default. Admin-only. */
        listStudentResearchPolicies: builder.query<ResearchStudentPolicyView[], void>({
            query: () => ({ url: "/research-studio/policy/students" }),
            providesTags: ["ResearchStudio"]
        }),
        setStudentResearchPolicyOverride: builder.mutation<
            ResearchPolicyOverride, { userId: string; override: ResearchPolicyOverride }
        >({
            query: ({ userId, override }) => ({ url: `/research-studio/policy/students/${userId}`, method: "PUT", body: override }),
            invalidatesTags: ["ResearchStudio"]
        }),
        /** Resets a student to the coaching's own default. */
        clearStudentResearchPolicyOverride: builder.mutation<void, string>({
            query: (userId) => ({ url: `/research-studio/policy/students/${userId}`, method: "DELETE" }),
            invalidatesTags: ["ResearchStudio"]
        }),
        researchCapabilities: builder.query<ResearchCapabilities, void>({
            query: () => ({ url: "/research-studio/capabilities" }),
            providesTags: ["ResearchStudio"]
        }),
        /** The platform owner's advanced-research switch (super admin only),
         *  mirroring getPaidAISwitch/setPaidAISwitch exactly. ON: every
         *  coaching's Advanced Research Mode is available regardless of that
         *  coaching's own policy. OFF (default): unchanged — a coaching only
         *  gets it if its own admin opted in via Research Studio's policy. */
        getAdvancedResearchSwitch: builder.query<
            { enabled: boolean; updated_at: string | null; updated_by: string | null },
            void
        >({
            query: () => ({ url: "/research-studio/advanced-switch" }),
            providesTags: ["ResearchStudio"]
        }),
        setAdvancedResearchSwitch: builder.mutation<
            { enabled: boolean; updated_at: string | null; updated_by: string | null },
            { enabled: boolean }
        >({
            query: (body) => ({ url: "/research-studio/advanced-switch", method: "PUT", body }),
            invalidatesTags: ["ResearchStudio"]
        }),
        listResearchProjects: builder.query<ResearchProject[], void>({
            query: () => ({ url: "/research-studio/projects" }),
            providesTags: ["ResearchStudio"]
        }),
        prepareResearchAnimation: builder.mutation<ArtefactPublic, { projectId: string; section_ids: string[] }>({
            query: ({ projectId, ...body }) => ({ url: `/research-studio/projects/${projectId}/animation`, method: "POST", body }),
            invalidatesTags: ["Artefacts"]
        }),
        researchProject: builder.query<ResearchProject, string>({
            query: (id) => ({ url: `/research-studio/projects/${id}` }),
            providesTags: ["ResearchStudio"]
        }),
        researchModels: builder.query<Pick<AIModelConfigPublic, "config_id" | "label" | "model_name" | "provider" | "scope" | "is_free">[], void>({
            query: () => ({ url: "/research-studio/advanced-models" }),
            providesTags: ["ResearchStudio"]
        }),
        // No `config_id`: the model comes from AI Model Settings' own
        // resolution (actor's key, then coaching's, then platform default),
        // never a picker on this page — see ResearchAdvancedPanel.
        startAdvancedResearch: builder.mutation<ResearchProject, {
            projectId: string; source_ids: string[]; acknowledged: boolean; resume: boolean;
            mode?: "explore" | "write";
        }>({
            query: ({ projectId, ...body }) => ({ url: `/research-studio/projects/${projectId}/advanced`, method: "POST", body }),
            invalidatesTags: ["ResearchStudio"]
        }),
        cancelAdvancedResearch: builder.mutation<ResearchProject, string>({
            query: (id) => ({ url: `/research-studio/projects/${id}/advanced/cancel`, method: "POST" }),
            invalidatesTags: ["ResearchStudio"]
        }),
        applyAdvancedResearch: builder.mutation<ResearchProject, string>({
            query: (id) => ({ url: `/research-studio/projects/${id}/advanced/apply`, method: "POST" }),
            invalidatesTags: ["ResearchStudio"]
        }),
        listResearchers: builder.query<ResearcherSummary[], void>({
            query: () => ({ url: "/research-studio/researchers" }),
            providesTags: ["ResearchStudio"]
        }),
        createResearchProject: builder.mutation<ResearchProject, {
            topic: string;
            learning_goal: string;
            audience_level: string;
            language: string;
        }>({
            query: (body) => ({ url: "/research-studio/projects", method: "POST", body }),
            invalidatesTags: ["ResearchStudio"]
        }),
        updateResearchPlan: builder.mutation<ResearchProject, {
            projectId: string;
            outline: ResearchOutlineSection[];
            approved: boolean;
        }>({
            query: ({ projectId, ...body }) => ({
                url: `/research-studio/projects/${projectId}/plan`, method: "PUT", body
            }),
            invalidatesTags: ["ResearchStudio"]
        }),
        /** Edit topic / goal / level; `rebuild_plan` writes a new question map
         *  (evidence kept, report cleared). See ResearchBriefUpdate. */
        updateResearchBrief: builder.mutation<ResearchProject, {
            projectId: string;
            topic: string;
            learning_goal: string;
            audience_level: string;
            rebuild_plan: boolean;
        }>({
            query: ({ projectId, ...body }) => ({
                url: `/research-studio/projects/${projectId}/brief`, method: "PUT", body
            }),
            invalidatesTags: ["ResearchStudio"]
        }),
        // `premium` is the student's Research Mode. On a Research-level project it adds
        // PubMed Central and Crossref's citation check; the server decides whether the
        // account may have them, so asking is never enough.
        discoverResearchSources: builder.mutation<ResearchProject, { projectId: string; premium?: boolean }>({
            query: ({ projectId, premium = false }) => ({
                url: `/research-studio/projects/${projectId}/discover`, method: "POST",
                params: premium ? { premium: true } : undefined
            }),
            invalidatesTags: ["ResearchStudio"]
        }),
        draftResearchProject: builder.mutation<ResearchProject, { projectId: string; polish?: boolean }>({
            query: ({ projectId, polish = false }) => ({
                url: `/research-studio/projects/${projectId}/draft`, method: "POST", body: { polish }
            }),
            invalidatesTags: ["ResearchStudio"]
        }),
        polishResearchProject: builder.mutation<ResearchProject, string>({
            query: (projectId) => ({ url: `/research-studio/projects/${projectId}/polish`, method: "POST" }),
            invalidatesTags: ["ResearchStudio"]
        }),
        cancelResearchStep: builder.mutation<ResearchProject, string>({
            query: (projectId) => ({ url: `/research-studio/projects/${projectId}/cancel`, method: "POST" }),
            invalidatesTags: ["ResearchStudio"]
        }),
        reviewResearchClaim: builder.mutation<ResearchProject, {
            projectId: string;
            claimId: string;
            support: ResearchClaim["support"];
            review_note: string;
        }>({
            query: ({ projectId, claimId, ...body }) => ({
                url: `/research-studio/projects/${projectId}/claims/${claimId}`,
                method: "PUT",
                body
            }),
            invalidatesTags: ["ResearchStudio"]
        }),
        confirmAllResearchClaims: builder.mutation<ResearchProject, string>({
            query: (projectId) => ({ url: `/research-studio/projects/${projectId}/claims/confirm-all`, method: "POST", body: { acknowledged: true } }),
            invalidatesTags: ["ResearchStudio"]
        }),
        detachResearchSource: builder.mutation<ResearchProject, { projectId: string; sourceId: string }>({
            query: ({ projectId, sourceId }) => ({ url: `/research-studio/projects/${projectId}/sources/${encodeURIComponent(sourceId)}`, method: "DELETE" }),
            invalidatesTags: ["ResearchStudio"]
        }),
        updateResearchSection: builder.mutation<ResearchProject, { projectId: string; sectionId: string; body: string }>({
            query: ({ projectId, sectionId, body }) => ({ url: `/research-studio/projects/${projectId}/sections/${sectionId}`, method: "PUT", body: { body } }),
            invalidatesTags: ["ResearchStudio"]
        }),
        getStyledResearchReport: builder.query<StyledResearchReport, { projectId: string; style: ReferenceStyle }>({
            query: ({ projectId, style }) => `/research-studio/projects/${projectId}/styled-report?style=${style}`,
            providesTags: ["ResearchStudio"]
        }),
        saveResearchToLibrary: builder.mutation<ResearchLibrarySaveResult, { projectId: string } & ResearchLibrarySaveOptions>({
            query: ({ projectId, ...body }) => ({ url: `/research-studio/projects/${projectId}/save-to-library`, method: "POST", body }),
            invalidatesTags: ["ResearchStudio", "Sources"]
        }),
        appendResearchSection: builder.mutation<ResearchProject, { projectId: string; sectionId: string; text: string }>({
            query: ({ projectId, sectionId, text }) => ({
                url: `/research-studio/projects/${projectId}/sections/${sectionId}/append`, method: "POST", body: { text }
            }),
            invalidatesTags: ["ResearchStudio"]
        }),
        attachResearchLibrarySources: builder.mutation<ResearchProject, {
            projectId: string;
            sourceIds: string[];
        }>({
            query: ({ projectId, sourceIds }) => ({
                url: `/research-studio/projects/${projectId}/library-sources`,
                method: "POST",
                body: { source_ids: sourceIds }
            }),
            invalidatesTags: ["ResearchStudio"]
        }),
        submitResearchTeachBack: builder.mutation<ResearchTeachBack, { projectId: string; response: string }>({
            query: ({ projectId, response }) => ({
                url: `/research-studio/projects/${projectId}/teach-back`, method: "POST", body: { response }
            })
        }),
        /** Research report -> private 10-question practice test (30-Sep-2026). */
        checkResearchEvidence: builder.mutation<ResearchProject, string>({
            query: (projectId) => ({ url: `/research-studio/projects/${projectId}/evidence-strength`, method: "POST" })
        }),
        createResearchPracticeTest: builder.mutation<SourcePracticeDraftPublic, string>({
            query: (projectId) => ({ url: `/research-studio/projects/${projectId}/practice-test`, method: "POST" })
        }),
        sendResearchDiscourse: builder.mutation<ResearchProject, { projectId: string; message: string }>({
            query: ({ projectId, message }) => ({
                url: `/research-studio/projects/${projectId}/discourse`, method: "POST", body: { message }
            }),
            invalidatesTags: ["ResearchStudio"]
        }),
        deleteResearchProject: builder.mutation<void, string>({
            query: (projectId) => ({ url: `/research-studio/projects/${projectId}`, method: "DELETE" }),
            invalidatesTags: ["ResearchStudio"]
        }),
        /** Read a chapter into the model's cache before the student asks.
         *
         *  Measured: first question against a chapter costs ~36s, every one
         *  after ~3s. Called when a notebook is OPENED so that cost lands while
         *  they are still choosing what to type. Fire and forget — the UI must
         *  never wait for it, and works identically if it fails. */
        warmNotebook: builder.mutation<{ status: string }, string>({
            query: (id) => ({ url: `/notebooks/${id}/warm`, method: "POST" })
        }),
        deleteNotebook: builder.mutation<void, string>({
            query: (id) => ({ url: `/notebooks/${id}`, method: "DELETE" }),
            // Sources too: deleting a notebook detaches its documents, so the
            // list they appear in changes even though none were removed.
            invalidatesTags: ["Notebooks", "Sources"]
        }),
        assignSourceToNotebook: builder.mutation<void, { notebookId: string; sourceId: string }>({
            query: ({ notebookId, sourceId }) => ({
                url: `/notebooks/${notebookId}/sources/${sourceId}`,
                method: "PUT"
            }),
            invalidatesTags: ["Notebooks", "Sources"]
        }),
        /** A coaching's uploaded study material. */
        listSources: builder.query<SourcePublic[], string | void>({
            query: (notebookId) => ({
                url: "/sources",
                params: notebookId ? { notebook_id: notebookId } : undefined
            }),
            providesTags: ["Sources"]
        }),
        listLibrary: builder.query<LibraryItemPublic[], void>({
            query: () => ({ url: "/sources/library" }),
            providesTags: ["Sources"]
        }),
        shareSourceWithSections: builder.mutation<unknown, { sourceId: string; section_ids: string[] }>({
            query: ({ sourceId, section_ids }) => ({ url: `/sources/${sourceId}/sections`, method: "POST", body: { section_ids } }),
            invalidatesTags: ["Sources"]
        }),
        listContributionRewards: builder.query<ContributionRewardPublic[], void>({
            query: () => ({ url: "/sources/rewards" })
        }),
        /** Deliberately invalidates nothing: it fires on every page turn, and
         *  refetching the whole library each time would be pure churn. The
         *  page refetches the listing when the reader closes instead. */
        saveReadingProgress: builder.mutation<ReadingProgressPublic, { sourceId: string; offset: number; page?: number | null; sections_in_view: number }>({
            query: ({ sourceId, ...body }) => ({
                url: `/sources/library/${sourceId}/progress`, method: "PUT", body
            })
        }),
        /** Super admin only: institute-approved material awaiting global publication. */
        listPlatformCandidates: builder.query<SourcePublic[], void>({
            query: () => ({ url: "/sources/review/platform-candidates" }),
            providesTags: ["Sources"]
        }),
        readLibraryPages: builder.query<LibraryPagePublic[], { sourceId: string; offset?: number; limit?: number }>({
            query: ({ sourceId, offset = 0, limit = 20 }) => ({
                url: `/sources/library/${sourceId}/pages`, params: { offset, limit }
            })
        }),
        /** The "My notes" dashboard: one summary per document or project with notes. */
        listMyNotes: builder.query<MyNotesCard[], void>({
            query: () => ({ url: "/annotations/notebooks" }),
            providesTags: [{ type: "Annotations", id: "NOTEBOOKS" }]
        }),
        /** Highlights and notes on one document or project, private to the caller. */
        listAnnotations: builder.query<AnnotationPublic[], AnnotationTarget>({
            query: ({ targetId, targetType }) => ({ url: "/annotations", params: { target_id: targetId, target_type: targetType } }),
            providesTags: (_result, _error, { targetId }) => [{ type: "Annotations", id: targetId }]
        }),
        /** Optimistic: a highlight must appear under the finger, not after a round trip. */
        createAnnotation: builder.mutation<AnnotationPublic, AnnotationCreate>({
            query: (body) => ({ url: "/annotations", method: "POST", body }),
            invalidatesTags: [{ type: "Annotations", id: "NOTEBOOKS" }],
            async onQueryStarted(body, { dispatch, queryFulfilled }) {
                const key: AnnotationTarget = { targetId: body.target_id, targetType: body.target_type ?? "library_source" };
                const temp: AnnotationPublic = {
                    ...body, annotation_id: `pending-${Date.now()}-${Math.random()}`,
                    target_type: key.targetType, kind: body.note ? "note" : "highlight"
                };
                const patch = dispatch(api.util.updateQueryData("listAnnotations", key, (draft) => { draft.push(temp); }));
                try {
                    const { data } = await queryFulfilled;
                    dispatch(api.util.updateQueryData("listAnnotations", key, (draft) => {
                        const at = draft.findIndex((row) => row.annotation_id === temp.annotation_id);
                        if (at >= 0) draft[at] = data;
                    }));
                } catch {
                    patch.undo();
                }
            }
        }),
        updateAnnotation: builder.mutation<AnnotationPublic, { annotationId: string; target: AnnotationTarget; color?: HighlightColor; note?: string }>({
            query: ({ annotationId, color, note }) => ({ url: `/annotations/${annotationId}`, method: "PATCH", body: { color, note } }),
            async onQueryStarted({ annotationId, target, color, note }, { dispatch, queryFulfilled }) {
                const patch = dispatch(api.util.updateQueryData("listAnnotations", target, (draft) => {
                    const row = draft.find((item) => item.annotation_id === annotationId);
                    if (!row) return;
                    if (color) row.color = color;
                    if (note !== undefined) { row.note = note; row.kind = note ? "note" : "highlight"; }
                }));
                try { await queryFulfilled; } catch { patch.undo(); }
            }
        }),
        deleteAnnotation: builder.mutation<void, { annotationId: string; target: AnnotationTarget }>({
            query: ({ annotationId }) => ({ url: `/annotations/${annotationId}`, method: "DELETE" }),
            invalidatesTags: [{ type: "Annotations", id: "NOTEBOOKS" }],
            async onQueryStarted({ annotationId, target }, { dispatch, queryFulfilled }) {
                const patch = dispatch(api.util.updateQueryData("listAnnotations", target, (draft) => {
                    const at = draft.findIndex((item) => item.annotation_id === annotationId);
                    if (at >= 0) draft.splice(at, 1);
                }));
                try { await queryFulfilled; } catch { patch.undo(); }
            }
        }),
        /** Topics come free from the headings; the summary appears once someone has asked for it. */
        getSourceGuide: builder.query<SourceGuidePublic, string>({
            query: (sourceId) => ({ url: `/sources/library/${sourceId}/guide` }),
            providesTags: (_r, _e, sourceId) => [{ type: "SourceGuide", id: sourceId }]
        }),
        writeSourceGuide: builder.mutation<SourceGuidePublic, { sourceId: string; force?: boolean }>({
            query: ({ sourceId, force }) => ({ url: `/sources/library/${sourceId}/guide`, method: "POST", params: force ? { force: true } : undefined }),
            invalidatesTags: (_r, _e, { sourceId }) => [{ type: "SourceGuide", id: sourceId }]
        }),
        compareLibraryEditions: builder.query<EditionComparisonPublic, string>({
            query: (sourceId) => ({ url: `/sources/library/${sourceId}/edition-comparison` })
        }),
        createScopePracticeDraft: builder.mutation<SourcePracticeDraftPublic, ScopePracticeRequest>({
            query: (body) => ({ url: `/sources/practice-draft`, method: "POST", body })
        }),
        /** A practice paper from the sections this student highlighted. */
        createHighlightPractice: builder.mutation<SourcePracticeDraftPublic, string>({
            query: (sourceId) => ({ url: `/sources/library/${sourceId}/practice-from-notes`, method: "POST" })
        }),
        createSourcePracticeDraft: builder.mutation<SourcePracticeDraftPublic, string>({
            query: (sourceId) => ({ url: `/sources/library/${sourceId}/practice-draft`, method: "POST" })
        }),
        reportLibraryItem: builder.mutation<{ report_id: string; status: string }, { sourceId: string; reason: string }>({
            query: ({ sourceId, reason }) => {
                const body = new FormData();
                body.append("reason", reason);
                return { url: `/sources/library/${sourceId}/report`, method: "POST", body };
            }
        }),
        listSourceReports: builder.query<SourceReportPublic[], void>({
            query: () => ({ url: "/sources/reports" })
        }),
        resolveSourceReport: builder.mutation<{ report_id: string; status: string }, { reportId: string; action: "dismiss" | "takedown"; note?: string }>({
            query: ({ reportId, action, note }) => ({ url: `/sources/reports/${reportId}/resolve`, method: "POST", body: { action, note } }),
            invalidatesTags: ["Sources"]
        }),
        uploadSource: builder.mutation<SourcePublic, FormData>({
            query: (body) => ({ url: "/sources", method: "POST", body }),
            invalidatesTags: ["Sources"]
        }),
        uploadSourceBatch: builder.mutation<SourcePublic, FormData>({
            query: (body) => ({ url: "/sources/batch", method: "POST", body }),
            invalidatesTags: ["Sources"]
        }),
        /** Polled while a document is processing — the row carries its own
         *  progress, so there is no separate job resource to track. */
        getSource: builder.query<SourcePublic, string>({
            query: (sourceId) => ({ url: `/sources/${sourceId}` })
        }),
        /** Re-run indexing over the stored file (POST /sources/{id}/retry). */
        retrySource: builder.mutation<SourcePublic, string>({
            query: (sourceId) => ({ url: `/sources/${sourceId}/retry`, method: "POST" }),
            invalidatesTags: ["Sources"]
        }),
        approveSource: builder.mutation<SourcePublic, string>({
            query: (sourceId) => ({ url: `/sources/${sourceId}/approve`, method: "POST" }),
            invalidatesTags: ["Sources"]
        }),
        submitSourceForReview: builder.mutation<{ review_state: string }, string>({
            query: (sourceId) => ({ url: `/sources/${sourceId}/share`, method: "POST" }),
            invalidatesTags: ["Sources"]
        }),
        approveSourceForSharing: builder.mutation<{ review_state: string }, string>({
            query: (sourceId) => ({ url: `/sources/${sourceId}/review/approve`, method: "POST" }),
            invalidatesTags: ["Sources"]
        }),
        publishSourceToPlatform: builder.mutation<{ source_id: string; visibility: string }, string>({
            query: (sourceId) => ({ url: `/sources/${sourceId}/review/platform-publish`, method: "POST" }),
            invalidatesTags: ["Sources"]
        }),
        takedownSource: builder.mutation<void, { sourceId: string; reason: string }>({
            query: ({ sourceId, reason }) => {
                const body = new FormData();
                body.append("reason", reason);
                return { url: `/sources/${sourceId}/takedown`, method: "POST", body };
            },
            invalidatesTags: ["Sources"]
        }),
        deleteSource: builder.mutation<void, string>({
            query: (sourceId) => ({ url: `/sources/${sourceId}`, method: "DELETE" }),
            invalidatesTags: ["Sources"]
        }),
        listQuizzes: builder.query<QuizSummary[], void>({
            query: () => ({ url: "/quizzes/" }),
            providesTags: ["Quiz"]
        }),
        createQuiz: builder.mutation<QuizSummary, QuizCreatePayload>({
            query: (body) => ({ url: "/quizzes/", method: "POST", body }),
            invalidatesTags: ["Quiz"]
        }),
        ingestQuizQuestions: builder.mutation<QuizIngestionPreview, FormData>({
            query: (body) => ({ url: "/quizzes/ingest", method: "POST", body })
        }),
        aiGenerateQuestions: builder.mutation<QuizIngestionPreview, QuizAIGenerateRequest>({
            query: (body) => ({ url: "/quizzes/ai-generate", method: "POST", body })
        }),
        listStudentAuthoredQuizzes: builder.query<StudentAuthoredQuizOverview[], void>({
            query: () => ({ url: "/quizzes/student-authored" }),
            providesTags: ["Quiz"]
        }),
        /** Author offers a finished private practice paper to their coaching.
         *  Enters the admin review queue — it publishes nothing on its own. */
        suggestQuizToCoaching: builder.mutation<QuizSummary, string>({
            query: (quizId) => ({ url: `/quizzes/${quizId}/suggest`, method: "POST" }),
            invalidatesTags: ["Quiz"]
        }),
        approveStudentQuiz: builder.mutation<QuizSummary, string>({
            query: (quizId) => ({ url: `/quizzes/${quizId}/approve`, method: "POST" }),
            invalidatesTags: ["Quiz"]
        }),
        /** Rewrite an UNPUBLISHED paper. Same payload as create, because it is
         *  the same builder screen — an edit form taking a different shape
         *  would drift from the thing that produced the paper. The server
         *  refuses anything that is not a draft. */
        /** One paper IN FULL, answer keys included, so the builder can reopen
         *  it. Distinct from `playQuiz`, which exists to hand a paper to a
         *  student and therefore strips every answer. */
        getQuizForEditing: builder.query<QuizSummary, string>({
            query: (quizId) => ({ url: `/quizzes/${quizId}/editable` }),
            providesTags: (_result, _error, quizId) => [{ type: "Quiz", id: quizId }]
        }),
        updateQuiz: builder.mutation<QuizSummary, { quizId: string; body: QuizCreatePayload }>({
            query: ({ quizId, body }) => ({ url: `/quizzes/${quizId}`, method: "PUT", body }),
            invalidatesTags: ["Quiz"]
        }),
        /** Hold a running paper so nobody NEW can start it. Students already
         *  sitting it are not interrupted — see `set_paused` in services/quiz.py
         *  for why the clock is deliberately left alone. */
        pauseQuiz: builder.mutation<QuizSummary, string>({
            query: (quizId) => ({ url: `/quizzes/${quizId}/pause`, method: "POST" }),
            invalidatesTags: ["Quiz"]
        }),
        resumeQuiz: builder.mutation<QuizSummary, string>({
            query: (quizId) => ({ url: `/quizzes/${quizId}/resume`, method: "POST" }),
            invalidatesTags: ["Quiz"]
        }),
        /** Author only. Archive, not delete: a shared paper carries other
         *  students' results. See `set_archived` in services/quiz.py. */
        archiveQuiz: builder.mutation<QuizSummary, { quizId: string; archived: boolean }>({
            query: ({ quizId, archived }) => ({
                url: `/quizzes/${quizId}/${archived ? "archive" : "unarchive"}`,
                method: "POST"
            }),
            invalidatesTags: ["Quiz"]
        }),
        playQuiz: builder.query<QuizPlayable, string>({
            query: (quizId) => ({ url: `/quizzes/${quizId}/play` }),
            providesTags: (_result, _error, quizId) => [{ type: "Quiz", id: quizId }]
        }),
        submitQuiz: builder.mutation<QuizAttemptResult, { quizId: string; answers: Record<string, QuizAnswerValue> }>({
            query: ({ quizId, answers }) => ({
                url: `/quizzes/${quizId}/submit`,
                method: "POST",
                body: { answers }
            }),
            invalidatesTags: (_result, _error, { quizId }) => ["Dashboard", { type: "Quiz", id: quizId }, "Attempts"]
        }),
        quizResult: builder.query<QuizResultPublic, string>({
            query: (quizId) => ({ url: `/quizzes/${quizId}/result` }),
            providesTags: (_result, _error, quizId) => [{ type: "Quiz", id: `result-${quizId}` }]
        }),
        /** The class view of one paper. Admin only — the server enforces it. */
        quizResultRoster: builder.query<
            QuizResultRoster,
            {
                quizId: string;
                skip?: number;
                limit?: number;
                search?: string;
                sort?: string;
                order?: "asc" | "desc";
            }
        >({
            query: ({ quizId, skip = 0, limit = 50, search, sort, order }) => ({
                url: `/quizzes/${quizId}/results`,
                // Empty values omitted: an empty `search=` is a different cache
                // key from no search, doubling the cached pages for nothing.
                params: {
                    skip,
                    limit,
                    ...(search ? { search } : {}),
                    ...(sort ? { sort } : {}),
                    ...(order ? { order } : {})
                }
            }),
            providesTags: (_r, _e, { quizId }) => [{ type: "Quiz", id: `roster-${quizId}` }]
        }),
        /** One named student's answer sheet, for an admin reviewing a dispute.
         *  Same shape as the student's own result, so it renders identically. */
        quizResultForStudent: builder.query<
            QuizResultPublic,
            { quizId: string; studentId: string }
        >({
            query: ({ quizId, studentId }) => ({
                url: `/quizzes/${quizId}/results/${studentId}`
            }),
            providesTags: (_r, _e, { quizId, studentId }) => [
                { type: "Quiz", id: `result-${quizId}-${studentId}` }
            ]
        }),
        doubtYouTube: builder.query<YouTubeRecommendation, string>({
            query: (doubtId) => `/ai/doubts/${encodeURIComponent(doubtId)}/youtube`,
            keepUnusedDataFor: 300
        }),
        /** The follow-up this student is most likely to ask next, shown as ghost
         *  text in the composer that Tab accepts. Cached on the server per answer;
         *  `null` when there is nothing to suggest (teacher mode, local model).
         *  Not tagged: it changes nothing any other query shows. */
        suggestNextQuestion: builder.mutation<{ suggestion: string | null }, string>({
            query: (doubtId) => ({
                url: `/ai/doubts/${doubtId}/next-question`,
                method: "POST"
            })
        }),
        /** Thumbs up / down on one of the student's own answers; null clears it. */
        rateDoubt: builder.mutation<
            { rating: "up" | "down" | null },
            { doubtId: string; rating: "up" | "down" | null; reason?: string; details?: string }
        >({
            query: ({ doubtId, rating, reason, details }) => ({
                url: `/ai/doubts/${doubtId}/rating`,
                method: "PUT",
                body: { rating, reason: reason ?? null, details: details ?? null }
            }),
            invalidatesTags: ["Doubts"]
        }),
        /** Questions where an independent re-solve disagreed with the answer
         *  key. Admin only — the payload contains the key itself. */
        keyVerificationQueue: builder.query<
            KeyVerificationQueue,
            { quizId: string; onlyDisputed?: boolean }
        >({
            query: ({ quizId, onlyDisputed = true }) => ({
                url: `/quizzes/${quizId}/key-verification`,
                params: { only_disputed: onlyDisputed }
            }),
            providesTags: (_r, _e, { quizId }) => [{ type: "Quiz", id: `keycheck-${quizId}` }]
        }),
        /** Generated solution methods awaiting review. Admin only. */
        solutionMethodQueue: builder.query<
            SolutionMethodQueue,
            { quizId: string; statusFilter?: string }
        >({
            query: ({ quizId, statusFilter }) => ({
                url: `/quizzes/${quizId}/solution-methods`,
                params: statusFilter ? { status_filter: statusFilter } : undefined
            }),
            providesTags: (_r, _e, { quizId }) => [{ type: "Quiz", id: `methods-${quizId}` }]
        }),
        reviewSolutionMethod: builder.mutation<
            SolutionMethod,
            { quizId: string } & SolutionMethodReviewRequest
        >({
            query: ({ quizId, ...body }) => ({
                url: `/quizzes/${quizId}/solution-methods/review`,
                method: "POST",
                body
            }),
            // Refetch the queue so a decided method leaves the list, and the
            // student-facing result too, since approving changes what it shows.
            invalidatesTags: (_r, _e, { quizId }) => [
                { type: "Quiz", id: `methods-${quizId}` },
                { type: "Quiz", id: `result-${quizId}` }
            ]
        }),
        quizItemAnalysis: builder.query<QuizItemAnalysisReport, string>({
            query: (quizId) => ({ url: `/quizzes/${quizId}/item-analysis` }),
            providesTags: (_r, _e, quizId) => [{ type: "Quiz", id: `items-${quizId}` }]
        }),
        mentorReport: builder.query<MentorReport, string>({
            query: (quizId) => ({ url: `/quizzes/${quizId}/mentor` }),
            providesTags: (_result, _error, quizId) => [{ type: "Quiz", id: `mentor-${quizId}` }]
        }),
        /** THE PAGED LIST ENDPOINTS.
         *
         *  Siblings of the unpaged ones, which stay for pickers and dropdowns
         *  that genuinely want every row. `buildListParams` drops empty values
         *  rather than sending them: an empty `search=` is a different cache
         *  key from no search, which would double the cached pages for nothing.
         */
        pageQuizzes: builder.query<Page<QuizSummary>, PagedListArgs | void>({
            query: (args) => ({ url: "/quizzes/page", params: buildListParams(args) }),
            providesTags: ["Quiz"]
        }),
        pageUsers: builder.query<Page<UserPublic>, PagedListArgs | void>({
            query: (args) => ({ url: "/users/page", params: buildListParams(args) }),
            providesTags: ["Users"]
        }),
        pageStudents: builder.query<Page<StudentRosterRow>, PagedListArgs | void>({
            query: (args) => ({ url: "/students/page", params: buildListParams(args) }),
            providesTags: ["Students"]
        }),
        pageSubjects: builder.query<Page<Subject>, PagedListArgs | void>({
            query: (args) => ({ url: "/subjects/page", params: buildListParams(args) }),
            providesTags: ["Subjects"]
        }),
        pageExams: builder.query<Page<ExamPublic>, PagedListArgs | void>({
            query: (args) => ({ url: "/exams/page", params: buildListParams(args) }),
            providesTags: ["Exams"]
        }),
        pageCoachings: builder.query<Page<CoachingPublic>, PagedListArgs | void>({
            query: (args) => ({ url: "/coaching/page", params: buildListParams(args) }),
            providesTags: ["Coachings"]
        }),
        myAttempts: builder.query<
            PagedAttempts,
            {
                limit?: number;
                offset?: number;
                search?: string;
                status?: string;
                sort?: string;
                order?: "asc" | "desc";
            } | void
        >({
            // Search, filter and sort go to the SERVER. Filtering the returned
            // page in the browser covered only the rows already fetched, so a
            // match on a later page was reported as "no matches".
            query: (args) => ({
                url: "/quizzes/attempts/me",
                params: {
                    limit: args?.limit ?? 20,
                    offset: args?.offset ?? 0,
                    // Omitted rather than sent empty: an empty `search=` is a
                    // different cache key from no search at all, which would
                    // double the cached pages for no reason.
                    ...(args?.search ? { search: args.search } : {}),
                    ...(args?.status ? { status: args.status } : {}),
                    ...(args?.sort ? { sort: args.sort } : {}),
                    ...(args?.order ? { order: args.order } : {})
                }
            }),
            providesTags: ["Attempts"]
        }),
        /** Ids only, and deliberately NOT paged: the exam list filters out
         *  papers already sat, so it needs the complete set. Paging it would
         *  make a paper finished months ago reappear as if it were new. */
        myAttemptedQuizIds: builder.query<string[], void>({
            query: () => ({ url: "/quizzes/attempts/me/ids" }),
            providesTags: ["Attempts"]
        }),
        notices: builder.query<Notice[], void>({
            query: () => ({ url: "/notices/" }),
            providesTags: ["Notice"]
        }),
        createNotice: builder.mutation<Notice, NoticeCreateRequest>({
            query: (body) => ({ url: "/notices/", method: "POST", body }),
            // The list on the dashboard is the only place a notice is visible,
            // so it has to refetch or the admin posts an announcement and sees
            // no evidence it worked.
            invalidatesTags: ["Notice"]
        }),
        adminAnalytics: builder.query<AdminAnalytics, { windowDays?: number; coachingId?: string } | void>({
            query: (args) => {
                const params: Record<string, string | number> = {};
                if (args && args.windowDays) {
                    params.window_days = args.windowDays;
                }
                // Super admins have no coaching_id of their own, so the backend
                // can't infer a scope — they must name the coaching explicitly.
                if (args && args.coachingId) {
                    params.coaching_id = args.coachingId;
                }
                return { url: "/dashboard/analytics", params: Object.keys(params).length ? params : undefined };
            },
            providesTags: ["Dashboard"]
        }),
        benchmarkReport: builder.query<BenchmarkReport, { coachingId?: string } | void>({
            query: (args) => ({
                url: "/dashboard/benchmark",
                params: args && args.coachingId ? { coaching_id: args.coachingId } : undefined
            }),
            providesTags: ["Dashboard", "Coachings"]
        }),
        notifications: builder.query<NotificationFeed, void>({
            query: () => ({ url: "/notifications/" }),
            providesTags: ["Notifications"]
        }),
        markNotificationRead: builder.mutation<NotificationFeed, string>({
            query: (notificationId) => ({ url: `/notifications/${notificationId}/read`, method: "POST" }),
            // Deliberately NOT invalidatesTags. Both read endpoints already return
            // the authoritative post-write feed, so writing that straight into the
            // cache is enough. An invalidation-triggered refetch fires within a
            // millisecond or two of the write and can read back the pre-write
            // state, leaving the badge stuck on a stale unread count until the
            // next poll.
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(api.util.updateQueryData("notifications", undefined, () => data));
                } catch {
                    // Leave the cache untouched; the next poll reconciles it.
                }
            }
        }),
        markAllNotificationsRead: builder.mutation<NotificationFeed, void>({
            query: () => ({ url: "/notifications/read-all", method: "POST" }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(api.util.updateQueryData("notifications", undefined, () => data));
                } catch {
                    // Leave the cache untouched; the next poll reconciles it.
                }
            }
        }),
        aiLegacyBuilder: builder.mutation<AILegacyBuilderResult, AILegacyBuilderPayload>({
            query: (body) => ({ url: "/ai/legacy-builder", method: "POST", body }),
            invalidatesTags: ["Quiz"]
        }),
        aiRuns: builder.query<any[], void>({
            query: () => ({ url: "/ai/runs" })
        }),
        /** Server-side logout. Revokes the presented tokens so the session
         *  cannot be resumed by anyone holding a copy of them. */
        logout: builder.mutation<void, { refresh_token?: string } | void>({
            query: (body) => ({ url: "/auth/logout", method: "POST", body: body ?? {} })
        }),
        /** Revoke every session this user has, on every device. */
        logoutEverywhere: builder.mutation<void, void>({
            query: () => ({ url: "/auth/logout-all", method: "POST" })
        }),
        askDoubt: builder.mutation<DoubtPublic, FormData>({
            query: (body) => ({ url: "/ai/doubt", method: "POST", body }),
            invalidatesTags: ["Doubts"]
        }),
        /** Preview which expert would answer, without answering.
         *  Classification is lexical — no model call, no tokens — so this is
         *  safe to call on a debounce while the student types. */
        classifyDomain: builder.mutation<DomainDetection, FormData>({
            query: (body) => ({ url: "/ai/classify-domain", method: "POST", body })
        }),
        /** Read a message for what it asks the product to DO ("explain X" vs
         *  "make me a quiz on X"). Lexical — no model call, no tokens — and it
         *  only ever proposes; the student confirms before anything is built. */
        readChatIntent: builder.mutation<ChatIntentResponse, { message: string }>({
            query: (body) => ({ url: "/ai/chat/intent", method: "POST", body })
        }),
        transcribeAudio: builder.mutation<TranscriptionPublic, FormData>({
            query: (body) => ({ url: "/ai/transcribe", method: "POST", body })
        }),
        listLanguages: builder.query<LanguageInfo[], void>({
            query: () => ({ url: "/users/languages" })
        }),
        /** Own password. Returns a fresh token pair: every other session is
         *  signed out, this one continues on the new tokens. */
        listClassSections: builder.query<ClassSection[], { year?: string } | void>({
            query: (args) => ({ url: "/school/sections", params: args && args.year ? { year: args.year } : undefined }),
            providesTags: ["Sections"]
        }),
        listAssignments: builder.query<Assignment[], void>({
            query: () => ({ url: "/school/assignments" }),
            providesTags: ["Work"]
        }),
        createAssignment: builder.mutation<Assignment, AssignmentPayload>({
            query: (body) => ({ url: "/school/assignments", method: "POST", body }),
            invalidatesTags: ["Work"]
        }),
        deleteAssignment: builder.mutation<void, string>({
            query: (id) => ({ url: `/school/assignments/${id}`, method: "DELETE" }),
            invalidatesTags: ["Work"]
        }),
        assignmentProgress: builder.query<AssignmentStudentStatus[], string>({
            query: (id) => ({ url: `/school/assignments/${id}/students` }),
            providesTags: ["Work"]
        }),
        myWork: builder.query<MyWorkItem[], void>({
            query: () => ({ url: "/school/my-work" }),
            providesTags: ["Work"]
        }),
        markWorkDone: builder.mutation<void, { id: string; note?: string; ai_use?: string[] }>({
            query: ({ id, note, ai_use }) => ({ url: `/school/my-work/${id}/done`, method: "POST", body: { note, ai_use } }),
            invalidatesTags: ["Work"]
        }),
        classReport: builder.query<ClassReport, void>({
            query: () => ({ url: "/school/reports/class" }),
            providesTags: ["Work"]
        }),
        schoolReport: builder.query<SchoolReport, void>({
            query: () => ({ url: "/school/reports/school" }),
            providesTags: ["Work"]
        }),
        organisationsReport: builder.query<OrganisationSummary[], void>({
            query: () => ({ url: "/reports/organisations" })
        }),
        myAccess: builder.query<MyAccess, void>({
            query: () => ({ url: "/school/my-access" }),
            providesTags: ["Access"]
        }),
        accessDefaults: builder.query<RoleAccessDefaults[], void>({
            query: () => ({ url: "/school/access/defaults" }),
            providesTags: ["Access"]
        }),
        setAccessDefaults: builder.mutation<RoleAccessDefaults, { role: string; switches: Record<string, boolean> }>({
            query: ({ role, switches }) => ({ url: `/school/access/defaults/${role}`, method: "PUT", body: switches }),
            invalidatesTags: ["Access"]
        }),
        listAcademicYears: builder.query<AcademicYear[], void>({
            query: () => ({ url: "/school/years" }),
            providesTags: ["Sections"]
        }),
        addAcademicYear: builder.mutation<AcademicYear, { year: string; starts_on?: string; ends_on?: string }>({
            query: (body) => ({ url: "/school/years", method: "POST", body }),
            invalidatesTags: ["Sections"]
        }),
        makeAcademicYearCurrent: builder.mutation<AcademicYear, string>({
            query: (year) => ({ url: `/school/years/${year}/current`, method: "POST" }),
            // The current year changes what every school screen lists.
            invalidatesTags: ["Sections", "Coachings", "Users"]
        }),
        rolloverAcademicYear: builder.mutation<RolloverResult, { year: string } & RolloverPayload>({
            query: ({ year, ...body }) => ({ url: `/school/years/${year}/rollover`, method: "POST", body }),
            invalidatesTags: ["Sections", "Users"]
        }),
        createClassSection: builder.mutation<ClassSection, ClassSectionPayload>({
            query: (body) => ({ url: "/school/sections", method: "POST", body }),
            invalidatesTags: ["Sections"]
        }),
        allocateClassSection: builder.mutation<
            ClassSection,
            { id: string; class_teacher_id?: string | null; teacher_ids: string[] }
        >({
            query: ({ id, ...body }) => ({ url: `/school/sections/${id}`, method: "PATCH", body }),
            invalidatesTags: ["Sections"]
        }),
        deleteClassSection: builder.mutation<void, string>({
            query: (id) => ({ url: `/school/sections/${id}`, method: "DELETE" }),
            invalidatesTags: ["Sections"]
        }),
        sectionRoster: builder.query<SectionStudent[], string>({
            query: (id) => ({ url: `/school/sections/${id}/students` }),
            providesTags: ["Sections"]
        }),
        placeStudent: builder.mutation<void, { studentId: string; class_section_id: string; roll_no?: string | null }>({
            query: ({ studentId, ...body }) => ({ url: `/school/enrolments/${studentId}`, method: "PUT", body }),
            invalidatesTags: ["Sections"]
        }),
        /** Teachers' accounts from a CSV - the student importer with `role=teacher`. */
        importTeachers: builder.mutation<StudentImportResult, FormData>({
            query: (body) => ({ url: "/students/import", method: "POST", params: { role: "teacher" }, body }),
            invalidatesTags: ["Users"]
        }),
        changePassword: builder.mutation<
            { access_token: string; refresh_token: string },
            { current_password: string; new_password: string }
        >({
            query: (body) => ({ url: "/auth/change-password", method: "POST", body })
        }),
        /** Own display name - every role, self-scoped. */
        setMyName: builder.mutation<UserPublic, string>({
            query: (full_name) => ({ url: "/users/me/name", method: "PUT", body: { full_name } })
        }),
        setMyLanguage: builder.mutation<UserPublic, string>({
            query: (preferred_language) => ({
                url: "/users/me/language",
                method: "PUT",
                body: { preferred_language }
            })
        }),
        myDoubts: builder.query<DoubtPublic[], void>({
            query: () => ({ url: "/ai/doubts/me" }),
            providesTags: ["Doubts"]
        }),
        /** Deletes a chat for the student (the platform keeps its record). */
        deleteConversation: builder.mutation<void, string>({
            query: (conversationId) => ({ url: `/ai/conversations/${encodeURIComponent(conversationId)}`, method: "DELETE" }),
            invalidatesTags: ["Doubts"]
        }),
        saveQuizProgress: builder.mutation<
            QuizProgressState,
            { quizId: string; answers: Record<string, QuizAnswerValue>; markedForReview?: string[] }
        >({
            query: ({ quizId, answers, markedForReview }) => ({
                url: `/quizzes/${quizId}/progress`,
                method: "POST",
                body: { answers, marked_for_review: markedForReview ?? [] }
            })
        }),
        /** Server-side neural TTS. Returns a WAV blob; a 503 means the server
         *  cannot synthesise and the caller should fall back to browser speech. */
        speakText: builder.mutation<Blob, { text: string; language: string }>({
            query: ({ text, language }) => ({
                url: "/ai/speak",
                method: "POST",
                body: { text, language },
                responseHandler: (response) => response.blob()
            })
        }),
        /** Which languages the server can actually speak, so the UI does not
         *  offer a play button that will only ever fall back. */
        speakLanguages: builder.query<string[], void>({
            query: () => ({ url: "/ai/speak/languages" })
        }),
        draftTranslations: builder.mutation<
            TranslationCoverage,
            { quizId: string; language: string; overwriteDrafts?: boolean }
        >({
            query: ({ quizId, language, overwriteDrafts }) => ({
                url: `/quizzes/${quizId}/translations/draft`,
                method: "POST",
                body: { language, overwrite_drafts: overwriteDrafts ?? false }
            }),
            invalidatesTags: ["Translations"]
        }),
        listTranslations: builder.query<TranslationPublic[], { quizId: string; language: string }>({
            query: ({ quizId, language }) => ({
                url: `/quizzes/${quizId}/translations`,
                params: { language }
            }),
            providesTags: ["Translations"]
        }),
        reviewTranslation: builder.mutation<
            TranslationPublic,
            { quizId: string; translationId: string; approve: boolean; text?: string; options?: string[] }
        >({
            query: ({ quizId, translationId, approve, text, options }) => ({
                url: `/quizzes/${quizId}/translations/review`,
                method: "POST",
                body: { translation_id: translationId, approve, text, options }
            }),
            invalidatesTags: ["Translations"]
        }),
        translationCoverage: builder.query<TranslationCoverage, { quizId: string; language: string }>({
            query: ({ quizId, language }) => ({
                url: `/quizzes/${quizId}/translations/coverage`,
                params: { language }
            }),
            providesTags: ["Translations"]
        }),
        telemetryInsights: builder.query<TelemetryInsights, string>({
            query: (quizId) => ({ url: `/quizzes/${quizId}/telemetry/insights` })
        }),
        feedback: builder.mutation<any, any>({
            query: (body) => ({ url: "/feedback/submit", method: "POST", body })
        }),
        importStudents: builder.mutation<StudentImportResult, FormData>({
            query: (body) => ({ url: "/students/import", method: "POST", body }),
            // A school roster also places students in sections (plan step 3).
            // The roster it just changed has to refetch, or the admin uploads
            // forty students and the table behind the panel still shows the
            // old count until they reload the page themselves.
            invalidatesTags: ["Users", "Students", "Sections"]
        }),
        createInvite: builder.mutation<InviteLink, { quizId: string; channel: string }>({
            query: ({ quizId, channel }) => ({
                url: `/quizzes/${quizId}/invites`,
                method: "POST",
                body: { quiz_id: quizId, channel }
            }),
            invalidatesTags: ["Quiz"]
        }),
        listInvites: builder.query<InviteLink[], string>({
            query: (quizId) => ({ url: `/quizzes/${quizId}/invites` }),
            providesTags: ["Quiz"]
        }),
        resolveInvite: builder.query<InviteDetails, string>({
            query: (inviteCode) => ({ url: `/quizzes/invites/${inviteCode}` })
        }),
        /** Joining a friend's challenge: this is what grants access. */
        acceptInvite: builder.mutation<{ quiz_id: string }, string>({
            query: (inviteCode) => ({ url: `/quizzes/invites/${inviteCode}/accept`, method: "POST" }),
            invalidatesTags: ["Quiz", "Challenge"]
        }),
        startGuestChallenge: builder.mutation<GuestChallengeSitting, string>({
            query: (inviteCode) => ({ url: `/quizzes/invites/${inviteCode}/guest/start`, method: "POST" })
        }),
        submitGuestChallenge: builder.mutation<{ score: number; correct: number; total_questions: number; board: ChallengeView }, { code: string; answers: Record<string, unknown> }>({
            query: ({ code, answers }) => ({ url: `/quizzes/invites/${code}/guest/submit`, method: "POST", body: { answers } }),
            invalidatesTags: ["Challenge"]
        }),
        guestChallengeBoard: builder.query<ChallengeView, string>({
            query: (inviteCode) => ({ url: `/quizzes/invites/${inviteCode}/guest/board` }),
            providesTags: ["Challenge"]
        }),
        challengeResults: builder.query<ChallengeView, string>({
            query: (quizId) => ({ url: `/quizzes/${quizId}/challenge` }),
            providesTags: ["Challenge"]
        }),
        logTelemetry: builder.mutation<any, { quizId: string; event_type: string; metadata?: Record<string, any> }>({
            query: ({ quizId, ...body }) => ({ url: `/quizzes/${quizId}/telemetry`, method: "POST", body })
        }),
        telemetrySnapshot: builder.query<TelemetrySnapshot, string>({
            query: (quizId) => ({ url: `/quizzes/${quizId}/telemetry` })
        }),
        submitExplanation: builder.mutation<ExplanationSubmission, { quiz_id: string; question_id: string; body: string }>({
            query: (body) => ({ url: "/explanations/", method: "POST", body }),
            invalidatesTags: ["Explanation"]
        }),
        pendingExplanations: builder.query<ExplanationSubmission[], void>({
            query: () => ({ url: "/explanations/pending" }),
            providesTags: ["Explanation"]
        }),
        reviewExplanation: builder.mutation<ExplanationSubmission, { submission_id: string; decision: string; notes?: string }>(
            {
                query: ({ submission_id, decision, notes }) => ({
                    url: `/explanations/${submission_id}/review`,
                    method: "POST",
                    body: { decision, notes }
                }),
                invalidatesTags: ["Explanation"]
            }
        ),
        apiTimings: builder.query<
            ApiTimingReport,
            { minutes?: number; limit?: number; slowestFirst?: boolean } | void
        >({
            query: (args) => ({
                url: "/events/api-timings",
                params: {
                    minutes: (args && args.minutes) || 60,
                    limit: (args && args.limit) || 100,
                    slowest_first: Boolean(args && args.slowestFirst)
                }
            })
        }),
        listAIModels: builder.query<AIModelConfigPublic[], void>({
            query: () => ({ url: "/ai-models/" }),
            providesTags: ["AIModels"]
        }),
        /** The chat's model picker: own models + the coaching's shared ones. */
        getChatModelChoices: builder.query<ChatModelChoices, void>({
            query: () => ({ url: "/ai-models/chat-choices" }),
            providesTags: ["AIModels"]
        }),
        /** Super admin: every connection, split by who added it, with usage. */
        aiModelOverview: builder.query<AIModelOverview, void>({
            query: () => ({ url: "/ai-models/overview" }),
            providesTags: ["AIModels"]
        }),
        /** The platform owner's paid-AI switch (super admin only). OFF sends every
         *  platform and coaching model to the free local one; a student's own
         *  key is not affected. */
        getPaidAISwitch: builder.query<
            { paid_models_enabled: boolean; updated_at: string | null; updated_by: string | null; platform_model: string | null },
            void
        >({
            query: () => ({ url: "/ai-models/paid-switch" }),
            providesTags: ["AIModels"]
        }),
        setPaidAISwitch: builder.mutation<
            { paid_models_enabled: boolean; updated_at: string | null; updated_by: string | null; platform_model: string | null },
            { paid_models_enabled: boolean }
        >({
            query: (body) => ({ url: "/ai-models/paid-switch", method: "PUT", body }),
            invalidatesTags: ["AIModels"]
        }),
        /** Jev (TypeSafe AI) - fast yes/no and pick-one decisions, super admin only. */
        getDecisionModel: builder.query<DecisionModelState, void>({
            query: () => ({ url: "/ai-models/decision-model" }),
            providesTags: ["AIModels"]
        }),
        setDecisionModel: builder.mutation<DecisionModelState, { enabled: boolean; api_key?: string | null }>({
            query: (body) => ({ url: "/ai-models/decision-model", method: "PUT", body }),
            invalidatesTags: ["AIModels"]
        }),
        testDecisionModel: builder.mutation<{ ok: boolean; message: string; duration_ms?: number }, void>({
            query: () => ({ url: "/ai-models/decision-model/test", method: "POST" })
        }),
        listAIProviders: builder.query<ProviderCatalogEntry[], void>({
            query: () => ({ url: "/ai-models/providers" })
        }),
        /** Ask a provider which models this credential can actually use.
         *  Not cached and not tagged: the answer depends on a key that only
         *  exists in the form, and caching it across providers would show one
         *  provider's models under another. */
        discoverAIModels: builder.mutation<ModelDiscoveryResult, ModelDiscoveryRequest>({
            query: (body) => ({ url: "/ai-models/discover-models", method: "POST", body })
        }),
        createAIModel: builder.mutation<AIModelConfigPublic, AIModelConfigCreatePayload>({
            query: (body) => ({ url: "/ai-models/", method: "POST", body }),
            invalidatesTags: ["AIModels"]
        }),
        updateAIModel: builder.mutation<
            AIModelConfigPublic,
            { config_id: string; data: AIModelConfigUpdatePayload }
        >({
            query: ({ config_id, data }) => ({ url: `/ai-models/${config_id}`, method: "PATCH", body: data }),
            invalidatesTags: ["AIModels"]
        }),
        deleteAIModel: builder.mutation<void, string>({
            query: (configId) => ({ url: `/ai-models/${configId}`, method: "DELETE" }),
            invalidatesTags: ["AIModels"]
        }),
        moderateAIModel: builder.mutation<AIModelConfigPublic, { configId: string; action: "pause" | "resume" | "block" | "unblock"; reason?: string | null }>({
            query: ({ configId, action, reason }) => ({ url: `/ai-models/${configId}/moderate`, method: "POST", body: { action, reason: reason || null } }),
            invalidatesTags: ["AIModels"]
        }),
        setDefaultAIModel: builder.mutation<AIModelConfigPublic, string>({
            query: (configId) => ({ url: `/ai-models/${configId}/set-default`, method: "POST" }),
            invalidatesTags: ["AIModels"]
        }),
        listAIRouting: builder.query<SpecialisationInfo[], void>({
            query: () => ({ url: "/ai-models/routing" })
        }),
        /** How much the answer cache has actually saved. Measured, not
         *  estimated — see the endpoint docstring. */
        studentHeatmap: builder.query<StudentHeatmap, string>({
            query: (studentId) => ({ url: `/dashboard/students/${studentId}/heatmap` })
        }),
        aiBudget: builder.query<AIBudgetSummary, void>({
            query: () => ({ url: "/ai/budget" })
        }),
        usageByActor: builder.query<ActorSpendReport, void>({
            query: () => ({ url: "/ai/usage/by-actor" })
        }),
        answerCacheStats: builder.query<AnswerCacheStats, void>({
            query: () => ({ url: "/ai-models/answer-cache" })
        }),
        seedFreeAIModels: builder.mutation<AIModelConfigPublic[], void>({
            query: () => ({ url: "/ai-models/seed-free-defaults", method: "POST" }),
            invalidatesTags: ["AIModels"]
        }),
        testAIModel: builder.mutation<AIModelTestResult, string>({
            query: (configId) => ({ url: `/ai-models/${configId}/test`, method: "POST" })
        }),
        /** Areas with live counts, driving the "pick an area first" step.
         *
         *  Pass `education_level` to get counts for just that stage, with
         *  empty areas omitted — a Class 9 student is then never offered
         *  "Medical & Healthcare". The narrowing has to happen server-side to
         *  stay consistent with the subject list the next step renders.
         *
         *  Shares the Subjects tag so creating/editing a subject refreshes the
         *  counts rather than leaving a stale "24 subjects" label behind. */
        listSubjectCategories: builder.query<SubjectCategoryInfo[], { education_level?: EducationLevel } | void>({
            query: (filter) => ({ url: "/subjects/categories", params: filter ?? undefined }),
            providesTags: ["Subjects"]
        }),
        getMyOnboarding: builder.query<LearnerProfile, void>({
            query: () => ({ url: "/users/me/onboarding" }),
            providesTags: ["Onboarding"]
        }),
        updateMyOnboarding: builder.mutation<LearnerProfile, LearnerProfileUpdate>({
            query: (body) => ({ url: "/users/me/onboarding", method: "PUT", body }),
            invalidatesTags: ["Onboarding"]
        }),
        listSubjects: builder.query<Subject[], SubjectListFilter | void>({
            query: (filter) => ({ url: "/subjects/", params: filter ?? undefined }),
            providesTags: ["Subjects"]
        }),
        createSubject: builder.mutation<Subject, SubjectPayload>({
            query: (body) => ({ url: "/subjects/", method: "POST", body }),
            invalidatesTags: ["Subjects"]
        }),
        updateSubject: builder.mutation<Subject, { subject_id: string; data: Partial<SubjectPayload> }>({
            query: ({ subject_id, data }) => ({ url: `/subjects/${subject_id}`, method: "PATCH", body: data }),
            invalidatesTags: ["Subjects", "Exams"]
        }),
        deleteSubject: builder.mutation<void, string>({
            query: (subjectId) => ({ url: `/subjects/${subjectId}`, method: "DELETE" }),
            invalidatesTags: ["Subjects", "Exams"]
        }),
        attachSubject: builder.mutation<
            any,
            { subject_id: string; exam_id: string; weightage?: number; ordering?: number }
        >({
            query: ({ subject_id, ...body }) => ({ url: `/subjects/${subject_id}/attach`, method: "POST", body }),
            invalidatesTags: ["Subjects", "Exams"]
        }),
        attachSubjectToExam: builder.mutation<
            any,
            { exam_id: string; subject_id: string; weightage?: number; ordering?: number }
        >({
            query: ({ exam_id, subject_id, ...body }) => ({
                url: `/exams/${exam_id}/subjects/${subject_id}`,
                method: "POST",
                body
            }),
            invalidatesTags: ["Exams", "Subjects"]
        }),
        registerStudent: builder.mutation<UserPublic, StudentRegistrationPayload>({
            query: (body) => ({ url: "/users/register", method: "POST", body })
        }),
        publicCoachings: builder.query<CoachingDirectoryEntry[], void>({
            query: () => ({ url: "/coaching/public" })
        }),
        publicSections: builder.query<PublicSection[], string>({
            query: (coachingId) => ({ url: `/coaching/public/${encodeURIComponent(coachingId)}/sections` })
        }),
        listRegistrations: builder.query<RegistrationRequest[], { status?: "pending_approval" | "rejected" } | void>({
            query: (args) => ({ url: "/registrations", params: { status: (args && args.status) || "pending_approval" } }),
            providesTags: ["Registrations"]
        }),
        registrationCount: builder.query<{ pending: number }, void>({
            query: () => ({ url: "/registrations/count" }),
            providesTags: ["Registrations"]
        }),
        approveRegistration: builder.mutation<UserPublic, { id: string; class_section_id?: string; roll_no?: string }>({
            query: ({ id, ...body }) => ({ url: `/registrations/${id}/approve`, method: "POST", body }),
            invalidatesTags: ["Registrations", "Users", "Sections"]
        }),
        rejectRegistration: builder.mutation<void, { id: string; reason: string }>({
            query: ({ id, reason }) => ({ url: `/registrations/${id}/reject`, method: "POST", body: { reason } }),
            invalidatesTags: ["Registrations"]
        }),
        deleteRegistration: builder.mutation<void, string>({
            query: (id) => ({ url: `/registrations/${id}`, method: "DELETE" }),
            invalidatesTags: ["Registrations"]
        }),
        createAdmin: builder.mutation<UserPublic, AdminCreatePayload>({
            query: (body) => ({ url: "/users/admins", method: "POST", body }),
            invalidatesTags: ["Users"]
        }),
        createUser: builder.mutation<UserPublic, UserCreatePayload>({
            query: (body) => ({ url: "/users", method: "POST", body }),
            invalidatesTags: ["Users"]
        }),
        updateUser: builder.mutation<UserPublic, { user_id: string; data: UserUpdatePayload }>({
            query: ({ user_id, data }) => ({ url: `/users/${user_id}`, method: "PATCH", body: data }),
            invalidatesTags: ["Users"]
        }),
        disableUser: builder.mutation<void, string>({
            query: (userId) => ({ url: `/users/${userId}`, method: "DELETE" }),
            invalidatesTags: ["Users"]
        }),
        transitionStudentStatus: builder.mutation<UserPublic, { user_id: string; status: StudentStatus; reason?: string }>({
            query: ({ user_id, ...body }) => ({ url: `/users/${user_id}/status`, method: "POST", body }),
            invalidatesTags: ["Users"]
        }),
        listUsers: builder.query<UserPublic[], UserListQuery | void>({
            query: (filters) => ({ url: "/users", params: filters ?? undefined }),
            providesTags: ["Users"]
        }),
        listAvailableCoachingIds: builder.query<string[], void>({
            query: () => ({ url: "/users/available-coaching-ids", method: "GET" })
        }),
        listCoachings: builder.query<CoachingPublic[], void>({
            query: () => ({ url: "/coaching/" }),
            providesTags: ["Coachings"]
        }),
        createCoaching: builder.mutation<CoachingPublic, CoachingCreatePayload>({
            query: (body) => ({ url: "/coaching/", method: "POST", body }),
            invalidatesTags: ["Coachings"]
        }),
        createOrganisationWithAdmin: builder.mutation<OrganisationCreated, OrganisationCreatePayload>({
            query: (body) => ({ url: "/coaching/with-admin", method: "POST", body }),
            invalidatesTags: ["Coachings"]
        }),
        createCoachingWithId: builder.mutation<CoachingPublic, { coaching_id: string; payload: CoachingCreatePayload }>({
            query: ({ coaching_id, payload }) => ({ url: "/coaching/", method: "POST", params: { coaching_id }, body: payload }),
            invalidatesTags: ["Coachings"]
        }),
        updateCoaching: builder.mutation<CoachingPublic, { coaching_id: string; patch: CoachingUpdatePayload }>({
            query: ({ coaching_id, patch }) => ({ url: `/coaching/${coaching_id}`, method: "PATCH", body: patch }),
            invalidatesTags: ["Coachings", "Dashboard"]
        }),

        updateTenantBranding: builder.mutation<TenantBranding, { coaching_id: string; patch: TenantBrandingUpdate }>({
            query: ({ coaching_id, patch }) => ({ url: `/tenant/${coaching_id}/branding`, method: "PATCH", body: patch }),
            invalidatesTags: ["Tenant", "Coachings"]
        }),
        uploadTenantLogo: builder.mutation<TenantBranding, { coaching_id: string; file: File }>({
            query: ({ coaching_id, file }) => {
                const body = new FormData();
                body.append("file", file);
                return { url: `/tenant/${coaching_id}/logo`, method: "POST", body };
            },
            invalidatesTags: ["Tenant", "Coachings"]
        }),

        deleteCoaching: builder.mutation<void, string>({
            query: (coachingId) => ({ url: `/coaching/${coachingId}`, method: "DELETE" }),
            invalidatesTags: ["Coachings", "Dashboard"]
        }),
        approveCoaching: builder.mutation<CoachingPublic, string>({
            query: (coachingId) => ({ url: `/coaching/${coachingId}/approve`, method: "POST" }),
            invalidatesTags: ["Coachings", "Dashboard"]
        }),
        suspendCoaching: builder.mutation<CoachingPublic, string>({
            query: (coachingId) => ({ url: `/coaching/${coachingId}/suspend`, method: "POST" }),
            invalidatesTags: ["Coachings", "Dashboard"]
        }),
        reactivateCoaching: builder.mutation<CoachingPublic, string>({
            query: (coachingId) => ({ url: `/coaching/${coachingId}/reactivate`, method: "POST" }),
            invalidatesTags: ["Coachings", "Dashboard"]
        }),
        listExams: builder.query<ExamPublic[], void>({
            query: () => ({ url: "/exams/" }),
            providesTags: ["Exams"]
        }),
        createExam: builder.mutation<ExamPublic, ExamCreatePayload>({
            query: (body) => ({ url: "/exams/", method: "POST", body }),
            invalidatesTags: ["Exams"]
        })
    })
});


/** One route's timings over the requested window. */
export interface ApiTimingRoute {
    route: string;
    method: string;
    count: number;
    avg_ms: number;
    p50_ms: number;
    p95_ms: number;
    max_ms: number;
    db_ms: number;
    external_ms: number;
    errors: number;
    error_rate: number;
}

/** One individual request, as recorded by the middleware. */
export interface ApiTimingRow {
    request_id: string;
    method: string;
    path: string;
    route: string;
    status: number;
    duration_ms: number;
    db_ms: number;
    db_queries: number;
    external_ms: number;
    app_ms: number;
    tenant_slug: string | null;
    actor_id: string | null;
    received_at: string;
    at: string;
}

export interface ApiTimingReport {
    window_minutes: number;
    generated_at: string;
    routes: ApiTimingRoute[];
    recent: ApiTimingRow[];
    /** Provider circuits currently open, keyed by config id. */
    circuits: Record<string, { failures: number; open: boolean; retry_in: number }>;
    background_tasks: number;
}

export const {
    useApiTimingsQuery,
    useLoginMutation,
    useTenantResolveQuery,
    useUpdateTenantBrandingMutation,
    useUploadTenantLogoMutation,
    useDashboardOverviewQuery,
    useStreaksQuery,
    useMyLearningQuery,
    useMyHeatmapQuery,
    useLiveQuizzesQuery,
    useListQuizzesQuery,
    useCreateQuizMutation,
    useIngestQuizQuestionsMutation,
    useAiGenerateQuestionsMutation,
    useListStudentAuthoredQuizzesQuery,
    useApproveStudentQuizMutation,
    useUpdateQuizMutation,
    useGetQuizForEditingQuery,
    usePauseQuizMutation,
    useResumeQuizMutation,
    useArchiveQuizMutation,
    useSuggestQuizToCoachingMutation,
    usePlayQuizQuery,
    useQuizResultQuery,
    useQuizResultRosterQuery,
    useQuizResultForStudentQuery,
    useQuizItemAnalysisQuery,
    useSuggestNextQuestionMutation,
    useRateDoubtMutation,
    useDoubtYouTubeQuery,
    useKeyVerificationQueueQuery,
    useSolutionMethodQueueQuery,
    useReviewSolutionMethodMutation,
    useLazyMentorReportQuery,
    useMyAttemptsQuery,
    // The paged list endpoints, one per resource. See `buildListParams`.
    usePageQuizzesQuery,
    usePageUsersQuery,
    usePageStudentsQuery,
    usePageSubjectsQuery,
    usePageExamsQuery,
    usePageCoachingsQuery,
    useMyAttemptedQuizIdsQuery,
    useSubmitQuizMutation,
    useNoticesQuery,
    useCreateNoticeMutation,
    useAdminAnalyticsQuery,
    useBenchmarkReportQuery,
    useNotificationsQuery,
    useMarkNotificationReadMutation,
    useMarkAllNotificationsReadMutation,
    useAiLegacyBuilderMutation,
    useAiRunsQuery,
    useAskDoubtMutation,
    useReadChatIntentMutation,
    useLogoutMutation,
    useLogoutEverywhereMutation,
    useClassifyDomainMutation,
    useMyDoubtsQuery,
    useDeleteConversationMutation,
    useTranscribeAudioMutation,
    useListLanguagesQuery,
    useSetMyLanguageMutation,
    useSetMyNameMutation,
    useChangePasswordMutation,
    useSaveQuizProgressMutation,
    useSpeakTextMutation,
    useSpeakLanguagesQuery,
    useTelemetryInsightsQuery,
    useDraftTranslationsMutation,
    useListTranslationsQuery,
    useReviewTranslationMutation,
    useTranslationCoverageQuery,
    useFeedbackMutation,
    useImportStudentsMutation,
    useCreateInviteMutation,
    useListInvitesQuery,
    useResolveInviteQuery,
    useAcceptInviteMutation,
    useChallengeResultsQuery,
    useStartGuestChallengeMutation,
    useSubmitGuestChallengeMutation,
    useGuestChallengeBoardQuery,
    useLogTelemetryMutation,
    useTelemetrySnapshotQuery,
    useSubmitExplanationMutation,
    usePendingExplanationsQuery,
    useReviewExplanationMutation,
    useListAIModelsQuery,
    useGetChatModelChoicesQuery,
    useAiModelOverviewQuery,
    useGetPaidAISwitchQuery,
    useGetDecisionModelQuery,
    useModerateAIModelMutation,
    useSetDecisionModelMutation,
    useTestDecisionModelMutation,
    useSetPaidAISwitchMutation,
    useListAIProvidersQuery,
    useDiscoverAIModelsMutation,
    useCreateAIModelMutation,
    useUpdateAIModelMutation,
    useDeleteAIModelMutation,
    useSetDefaultAIModelMutation,
    useTestAIModelMutation,
    useListAIRoutingQuery,
    useListSourcesQuery,
    useListLibraryQuery,
    useListContributionRewardsQuery,
    useReadLibraryPagesQuery,
    useCompareLibraryEditionsQuery,
    useSaveReadingProgressMutation,
    useListAnnotationsQuery,
    useListMyNotesQuery,
    useGetSourceGuideQuery,
    useWriteSourceGuideMutation,
    useCreateAnnotationMutation,
    useUpdateAnnotationMutation,
    useDeleteAnnotationMutation,
    useListPlatformCandidatesQuery,
    useCreateSourcePracticeDraftMutation,
    useCreateScopePracticeDraftMutation,
    useReportLibraryItemMutation,
    useListSourceReportsQuery,
    useResolveSourceReportMutation,
    usePlanArtefactMutation,
    useListArtefactsQuery,
    useListChatArtefactsQuery,
    useAttachArtefactMutation,
    useGetArtefactQuery,
    useUpdateArtefactPlanMutation,
    useRenderArtefactMutation,
    useDeleteArtefactMutation,
    useListNotebooksQuery,
    useCreateNotebookMutation,
    useResearchPolicyQuery,
    useUpdateResearchPolicyMutation,
    useListStudentResearchPoliciesQuery,
    useSetStudentResearchPolicyOverrideMutation,
    useClearStudentResearchPolicyOverrideMutation,
    useResearchCapabilitiesQuery,
    useGetAdvancedResearchSwitchQuery,
    useSetAdvancedResearchSwitchMutation,
    useListResearchProjectsQuery,
    useResearchProjectQuery,
    usePrepareResearchAnimationMutation,
    useResearchModelsQuery,
    useStartAdvancedResearchMutation,
    useCancelAdvancedResearchMutation,
    useApplyAdvancedResearchMutation,
    useListResearchersQuery,
    useCreateResearchProjectMutation,
    useUpdateResearchPlanMutation,
    useUpdateResearchBriefMutation,
    useConfirmAllResearchClaimsMutation,
    useDetachResearchSourceMutation,
    useUpdateResearchSectionMutation,
    useSaveResearchToLibraryMutation,
    useGetStyledResearchReportQuery,
    useDiscoverResearchSourcesMutation,
    useDraftResearchProjectMutation,
    usePolishResearchProjectMutation,
    useCancelResearchStepMutation,
    useReviewResearchClaimMutation,
    useAppendResearchSectionMutation,
    useCreateHighlightPracticeMutation,
    useAttachResearchLibrarySourcesMutation,
    useSubmitResearchTeachBackMutation,
    useSendResearchDiscourseMutation,
    useCreateResearchPracticeTestMutation,
    useCheckResearchEvidenceMutation,
    useDeleteResearchProjectMutation,
    useWarmNotebookMutation,
    useDeleteNotebookMutation,
    useAssignSourceToNotebookMutation,
    useUploadSourceMutation,
    useUploadSourceBatchMutation,
    useApproveSourceMutation,
    useSubmitSourceForReviewMutation,
    useApproveSourceForSharingMutation,
    usePublishSourceToPlatformMutation,
    useTakedownSourceMutation,
    useDeleteSourceMutation,
    useRetrySourceMutation,
    useAnswerCacheStatsQuery,
    useUsageByActorQuery,
    useAiBudgetQuery,
    useStudentHeatmapQuery,
    useSeedFreeAIModelsMutation,
    useGetMyOnboardingQuery,
    useUpdateMyOnboardingMutation,
    useListSubjectCategoriesQuery,
    useListSubjectsQuery,
    useCreateSubjectMutation,
    useUpdateSubjectMutation,
    useDeleteSubjectMutation,
    useAttachSubjectMutation,
    useAttachSubjectToExamMutation,
    useLogNotFoundEventMutation,
    useRegisterStudentMutation,
    usePublicCoachingsQuery,
    useCreateAdminMutation,
    useCreateUserMutation,
    useUpdateUserMutation,
    useDisableUserMutation,
    useTransitionStudentStatusMutation,
    useListUsersQuery,
    useLazyListUsersQuery,
    useListAvailableCoachingIdsQuery,
    useLazyListAvailableCoachingIdsQuery,
    useListCoachingsQuery,
    useListClassSectionsQuery,
    useShareSourceWithSectionsMutation,
    usePublicSectionsQuery,
    useListRegistrationsQuery,
    useRegistrationCountQuery,
    useApproveRegistrationMutation,
    useRejectRegistrationMutation,
    useDeleteRegistrationMutation,
    useListAcademicYearsQuery,
    useMyAccessQuery,
    useListAssignmentsQuery,
    useCreateAssignmentMutation,
    useDeleteAssignmentMutation,
    useAssignmentProgressQuery,
    useMyWorkQuery,
    useMarkWorkDoneMutation,
    useClassReportQuery,
    useSchoolReportQuery,
    useOrganisationsReportQuery,
    useAccessDefaultsQuery,
    useSetAccessDefaultsMutation,
    useAddAcademicYearMutation,
    useMakeAcademicYearCurrentMutation,
    useRolloverAcademicYearMutation,
    useCreateClassSectionMutation,
    useAllocateClassSectionMutation,
    useDeleteClassSectionMutation,
    useSectionRosterQuery,
    usePlaceStudentMutation,
    useImportTeachersMutation,
    useCreateCoachingMutation,
    useCreateCoachingWithIdMutation,
    useCreateOrganisationWithAdminMutation,
    useUpdateCoachingMutation,
    useDeleteCoachingMutation,
    useApproveCoachingMutation,
    useSuspendCoachingMutation,
    useReactivateCoachingMutation,
    useListExamsQuery,
    useCreateExamMutation
} = api;
